/* eslint-disable -- UX wireframe; not held to app lint rules */
import { useState, useRef } from "react";
import { Flowchart } from "../../shared/Flowchart";
import { Row, Box, MockBtn, MockField, MockBadge, WF } from "../../shared/WireframePrimitives";
import { InspectProvider, InspectorPanel, Inspect, useInspector } from "../../shared/Inspector";
import WizardChrome from "../../shared/WizardChrome";
import { WIZARD_TABS } from "../../shared/wizardTabs";

// ─── BRAND TOKENS ─────────────────────────────────────────────────────────────
const T = {
  brand:"#5e4ce2",brandHover:"#5046d4",brandSoft:"rgba(94,76,226,0.12)",brandBorder:"rgba(94,76,226,0.28)",
  cyan:"#06b6d4",cyanSoft:"rgba(6,182,212,0.1)",teal:"#14b8a6",tealSoft:"rgba(20,184,166,0.1)",
  amber:"#f59e0b",amberSoft:"rgba(245,158,11,0.12)",coral:"#f97316",coralSoft:"rgba(249,115,22,0.1)",
  green:"#22c55e",greenSoft:"rgba(34,197,94,0.1)",red:"#ef4444",redSoft:"rgba(239,68,68,0.1)",violet:"#7c3aed",
  lCanvas:"#f8fafc",lSurface:"#ffffff",lMuted:"#f1f5f9",lBorder:"rgba(51,65,85,0.1)",lBorderMd:"rgba(51,65,85,0.18)",
  lText:"#0f172a",lTextSec:"#475569",lTextMuted:"#94a3b8",
  dCanvas:"#0a0d12",dSurface:"#111318",dElevated:"#181c23",dMuted:"#1e2330",
  dBorder:"rgba(255,255,255,0.08)",dBorderMd:"rgba(255,255,255,0.14)",
  dText:"#f1f5f9",dTextSec:"#b0bec5",dTextMuted:"#64748b",
  fast:"150ms",mid:"220ms",slow:"320ms",
  ease:"cubic-bezier(0.4,0,0.2,1)",spring:"cubic-bezier(0.34,1.56,0.64,1)",
  rsm:"6px",rmd:"10px",rlg:"16px",rxl:"24px",
};
function useTheme(dark){
  return {
    bg:dark?T.dCanvas:T.lCanvas,surface:dark?T.dSurface:T.lSurface,
    elevated:dark?T.dElevated:T.lSurface,muted:dark?T.dMuted:T.lMuted,
    border:dark?T.dBorder:T.lBorder,borderMd:dark?T.dBorderMd:T.lBorderMd,
    text:dark?T.dText:T.lText,textSec:dark?T.dTextSec:T.lTextSec,textMuted:dark?T.dTextMuted:T.lTextMuted,
    sidebar:dark?"#0d1017":T.lSurface,
    dark,tr:`all ${T.mid} ${T.ease}`,trSlow:`all ${T.slow} ${T.ease}`,
  };
}

// ─── ZiniPixels Shell ──────────────────────────────────────────────────────────
const ZiniPixelsShell = ({ dark, children, viewport, activeTab = "projects", role = "editor" }) => {
  const th = useTheme(dark);
  const mob = viewport === "mobile";
  const viewer = role === "viewer";

  if (mob) {
    return (
      <div style={{ display:"flex", flexDirection:"column", minHeight:"100%", background:th.bg }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.brand }}>ZiniPixels</div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <div style={{ width:28, height:28, borderRadius:"50%", background:T.brandSoft, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:T.brand, cursor:"default" }}>U</div>
          </div>
        </div>
        <div style={{ flex:1, display:"flex", flexDirection:"column" }}>{children}</div>
        <div style={{ display:"flex", borderTop:`1px solid ${th.border}`, background:th.surface }}>
          {[
            { key:"projects", label:"Projects", icon:"📁" },
            { key:"recent", label:"Recent", icon:"🕐" },
            { key:"settings", label:"Settings", icon:"⚙️" },
          ].map(tab => (
            <div key={tab.key} style={{ flex:1, textAlign:"center", padding:"8px 0", fontSize:10, color:tab.key===activeTab?T.brand:th.textMuted, borderTop:tab.key===activeTab?`2px solid ${T.brand}`:"2px solid transparent", cursor:"default" }}>
              <div style={{ fontSize:16,marginBottom:2 }}>{tab.icon}</div>
              {tab.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%", background:th.bg }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 20px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ fontSize:16, fontWeight:700, color:T.brand, cursor:"default" }}>ZiniPixels</div>
          <div style={{ fontSize:12, color:th.textSec, padding:"3px 8px", background:th.muted, borderRadius:5, cursor:"default" }}>
            Acme Corp ▾
          </div>
        </div>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          {viewer && <MockBadge type="expiring" label="View only" />}
          <div style={{ width:30, height:30, borderRadius:"50%", background:T.brandSoft, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:T.brand, cursor:"default" }}>U</div>
        </div>
      </div>
      <div style={{ flex:1, display:"flex" }}>{children}</div>
    </div>
  );
};

// ─── ROUTES ────────────────────────────────────────────────────────────────────
const ROUTES = [
  { path:"/", comp:"DashboardPage", shell:"ZiniPixelsShell", auth:"protected", app:"zinipixels", note:"Landing page — project grid or empty state." },
  { path:"/project/:projectId", comp:"ProjectViewPage", shell:"ZiniPixelsShell", auth:"protected", app:"zinipixels", note:"Project detail — file tree sidebar + design canvas." },
  { path:"/project/:projectId/file/:fileId", comp:"CanvasEditorPage", shell:"ZiniPixelsShell", auth:"protected", app:"zinipixels", note:"Full design canvas editor with toolbar, layers, properties panels." },
  { path:"/project/:projectId/settings", comp:"ProjectSettingsPage", shell:"ZiniPixelsShell", auth:"protected", app:"zinipixels", note:"Project members, access, billing, and settings." },
  { path:"/project/:projectId/comments", comp:"CommentsReviewPage", shell:"ZiniPixelsShell", auth:"protected", app:"zinipixels", note:"Comments and review mode for the project." },
  { path:"/settings", comp:"SettingsPage", shell:"ZiniPixelsShell", auth:"protected", app:"zinipixels", note:"User profile, workspace settings, billing." },
  { path:"/onboarding", comp:"OnboardingPage", shell:"ZiniPixelsShell", auth:"protected", app:"zinipixels", note:"Onboarding flow — welcome, plan selection, payment." },
];

// ─── SHELLS ────────────────────────────────────────────────────────────────────
const SHELLS = [
  {
    id:"zinipixels-shell", label:"ZiniPixelsShell", who:"Designer / Developer / Viewer / Admin",
    topbar:"ZiniPixels logo · Workspace switcher · Role badge · User avatar",
    content:"Desktop: topbar + full-height. Mobile: topbar + bottom tab bar (Projects, Recent, Settings).",
    note:"No sidebar on dashboard. Sidebar appears inside project view (file tree).",
  },
];

// ─── IA DATA ───────────────────────────────────────────────────────────────────
const IA_GROUPS = [
  {
    id:"project-dashboard", label:"Project Dashboard", icon:"📁",
    note:"Landing page after sign-in. Entry point to the entire app.",
    screens:[
      { id:"dashboard-empty", label:"Dashboard — empty state", tag:"state" },
      { id:"dashboard-populated", label:"Dashboard — project grid", tag:"core" },
      { id:"dashboard-list-view", label:"Dashboard — list view", tag:"core" },
      { id:"dashboard-loading", label:"Dashboard — loading skeleton", tag:"state" },
      { id:"dashboard-error", label:"Dashboard — API error", tag:"state" },
      { id:"create-project-modal", label:"Create project modal", tag:"modal" },
      { id:"rename-project-modal", label:"Rename project modal", tag:"modal" },
      { id:"archive-project-modal", label:"Archive project confirmation", tag:"modal" },
      { id:"delete-project-modal", label:"Delete project confirmation", tag:"modal" },
      { id:"duplicate-project-toast", label:"Duplicate project toast", tag:"state" },
      { id:"project-view-empty", label:"Project view — no files", tag:"state" },
      { id:"project-view-populated", label:"Project view — file tree + placeholder", tag:"core" },
      { id:"project-view-loading", label:"Project view — loading", tag:"state" },
      { id:"viewer-dashboard", label:"Dashboard — Viewer role", tag:"core" },
      { id:"viewer-project-view", label:"Project view — Viewer role", tag:"core" },
    ],
  },
  {
    id:"design-canvas", label:"Design Canvas", icon:"✏️",
    note:"The core editor — tldraw-based canvas with tools, shapes, layers, and properties.",
    screens:[
      { id:"canvas-empty", label:"Empty canvas with grid background", tag:"core" },
      { id:"canvas-toolbar", label:"Canvas with drawing toolbar active", tag:"core" },
      { id:"canvas-shapes", label:"Canvas with shapes, text, connectors", tag:"core" },
      { id:"canvas-properties", label:"Properties panel open on right", tag:"core" },
      { id:"canvas-layers", label:"Layers panel open", tag:"core" },
      { id:"canvas-export", label:"Export dialog overlay", tag:"modal" },
      { id:"canvas-undo-redo", label:"Undo/redo toast and history indicator", tag:"state" },
      { id:"canvas-mobile", label:"Mobile canvas view with compact toolbar", tag:"core" },
    ],
  },
  {
    id:"collaboration", label:"Collaboration", icon:"👥",
    note:"Multiplayer editing with Yjs — real-time cursors, presence, conflict resolution.",
    screens:[
      { id:"collab-cursors", label:"Multiple user cursors and selections", tag:"core" },
      { id:"collab-presence", label:"Presence avatars in topbar + active users list", tag:"core" },
      { id:"collab-active-files", label:"Active file indicators — who's editing what", tag:"core" },
    ],
  },
  {
    id:"comments-review", label:"Comments & Review", icon:"💬",
    note:"Annotation, threaded comments, review mode with resolve workflow.",
    screens:[
      { id:"comment-create", label:"Create comment on canvas element", tag:"core" },
      { id:"comment-thread", label:"Comment thread sidebar panel", tag:"core" },
      { id:"comment-review-mode", label:"Review mode with annotation badges", tag:"core" },
      { id:"comment-resolve", label:"Resolved thread state", tag:"state" },
    ],
  },
  {
    id:"access-sharing", label:"Access Management & Sharing", icon:"🔗",
    note:"Project members, role-based access, invites, and access control.",
    screens:[
      { id:"sharing-modal", label:"Share/invite modal with permission levels", tag:"modal" },
      { id:"members-list", label:"Project members list with roles", tag:"core" },
      { id:"invite-pending", label:"Pending invite state with expiry", tag:"state" },
      { id:"access-denied", label:"Access denied — no permission screen", tag:"state" },
    ],
  },
  {
    id:"ai-connectors", label:"AI Connectors", icon:"🤖",
    note:"AI-powered design generation, suggestions, and chat assistant.",
    screens:[
      { id:"ai-panel", label:"AI assistant panel (chat interface)", tag:"core" },
      { id:"ai-generate", label:"AI generate design dialog with prompts", tag:"modal" },
      { id:"ai-suggestions", label:"AI suggestions inline overlay on canvas", tag:"core" },
      { id:"ai-history", label:"AI generation history panel", tag:"core" },
    ],
  },
  {
    id:"design-system", label:"Design System", icon:"🎨",
    note:"Design tokens, component library, theme management, and brand assets.",
    screens:[
      { id:"ds-panel", label:"Design system panel (right sidebar)", tag:"core" },
      { id:"ds-components", label:"Component library browser", tag:"core" },
      { id:"ds-tokens", label:"Token viewer and editor", tag:"core" },
      { id:"ds-themes", label:"Theme/swatch selector", tag:"core" },
    ],
  },
  {
    id:"settings", label:"Settings & Admin", icon:"⚙️",
    note:"Workspace settings, billing, member management, user profile.",
    screens:[
      { id:"settings-general", label:"General workspace settings", tag:"core" },
      { id:"settings-members", label:"Member management (admin)", tag:"core" },
      { id:"settings-billing", label:"Billing & plan overview", tag:"core" },
      { id:"settings-profile", label:"User profile and preferences", tag:"core" },
    ],
  },
  {
    id:"onboarding-billing", label:"Onboarding & Billing", icon:"🚀",
    note:"Welcome flow, plan selection, payment setup, billing portal.",
    screens:[
      { id:"onboarding-welcome", label:"Welcome/onboarding intro step", tag:"core" },
      { id:"onboarding-plan", label:"Plan selection screen", tag:"core" },
      { id:"onboarding-payment", label:"Payment method setup", tag:"core" },
      { id:"billing-portal", label:"Billing portal summary", tag:"core" },
    ],
  },
];

// ─── FLOWS ─────────────────────────────────────────────────────────────────────
const FLOWS = [
  {
    id:"first-login-empty-state", label:"First login — empty dashboard", who:"designer", color:"T.brand",
    steps:[
      { id:"step-sign-in", label:"Sign in via Clerk", shell:"ZiniPixelsShell", note:"Login page with email/password or Google OAuth. Redirects to dashboard on success." },
      { id:"step-empty-dashboard", label:"Empty project dashboard", shell:"ZiniPixelsShell", note:"Centered illustration, 'No projects yet' heading, subtext, large 'Create Project' CTA." },
      { id:"step-create-first-project", label:"Create project modal", shell:"ZiniPixelsShell", note:"Modal with name field + optional description. Creates first project card in grid." },
      { id:"step-project-created", label:"Dashboard with one project", shell:"ZiniPixelsShell", note:"Single project card with auto-generated thumbnail, name, '0 files', '1 member'." },
    ],
  },
  {
    id:"create-project-populated", label:"Create project in populated dashboard", who:"designer", color:"T.brand",
    steps:[
      { id:"step-existing-dashboard", label:"Populated dashboard", shell:"ZiniPixelsShell", note:"Grid of project cards. Top-right: Create button + search + grid/list toggle." },
      { id:"step-create-modal", label:"Create project modal with templates", shell:"ZiniPixelsShell", note:"Modal with name, description, optional template selector (Blank, Mobile app, Landing page, Dashboard)." },
      { id:"step-project-added", label:"Project appears in grid", shell:"ZiniPixelsShell", note:"New card animates in at top of grid with highlight. User can click to enter." },
    ],
  },
  {
    id:"enter-project", label:"Open project — file tree + placeholder", who:"both", color:"T.teal",
    steps:[
      { id:"step-click-project", label:"Click project card", shell:"ZiniPixelsShell", note:"Dashboard grid. Click any project card. Brief loading skeleton." },
      { id:"step-project-view", label:"File tree + placeholder canvas", shell:"ZiniPixelsShell", note:"Left sidebar (file tree with New File button). Main area: placeholder canvas — 'Design canvas coming in next phase'." },
    ],
  },
  {
    id:"manage-project-settings", label:"Rename, archive, or delete project", who:"admin", color:"T.amber",
    steps:[
      { id:"step-open-menu", label:"Open project context menu", shell:"ZiniPixelsShell", note:"3-dot menu on card: Rename, Duplicate, Archive, Delete (Owner/Admin only)." },
      { id:"step-rename-project", label:"Rename modal", shell:"ZiniPixelsShell", note:"Input pre-filled with current name. Save updates card in-place." },
      { id:"step-archive-project", label:"Archive confirmation", shell:"ZiniPixelsShell", note:"Confirm dialog: 'Archived projects move to trash for 30 days.' Undo toast after archiving." },
      { id:"step-delete-project", label:"Delete confirmation", shell:"ZiniPixelsShell", note:"Danger confirm: type 'DELETE' to enable red Delete button. Permanent action." },
    ],
  },
  {
    id:"viewer-browse-projects", label:"Viewer role — read-only browsing", who:"customer", color:"T.coral",
    steps:[
      { id:"step-viewer-dashboard", label:"Dashboard (Viewer)", shell:"ZiniPixelsShell", note:"'View only' badge on cards. Create button hidden. Menu only has 'View details'." },
      { id:"step-viewer-project-entry", label:"Project view (Viewer)", shell:"ZiniPixelsShell", note:"File tree visible but New File hidden. Files view-only. 'View only' indicator in topbar." },
    ],
  },
  {
    id:"design-canvas-flow", label:"Design Canvas — edit a file", who:"designer", color:"T.brand",
    gate:"Starter+",
    steps:[
      { id:"step-canvas-open", label:"Open file from project view", shell:"ZiniPixelsShell", note:"Click a file in the file tree sidebar. Canvas loads with grid background and toolbar." },
      { id:"step-canvas-empty", label:"Empty canvas ready", shell:"ZiniPixelsShell", note:"Grid background, top toolbar (Select, Draw, Text, Shape, Connector, Eraser), left file tree minimized. Tool tips visible." },
      { id:"step-canvas-draw-shape", label:"Draw shapes on canvas", shell:"ZiniPixelsShell", note:"Select Rectangle tool. Click-drag to draw. Shape appears with selection handles. Properties panel updates." },
      { id:"step-canvas-add-text", label:"Add text and connectors", shell:"ZiniPixelsShell", note:"Text tool to type 'Homepage'. Connector tool to link shapes. Labels on connectors." },
      { id:"step-canvas-layers", label:"Layers panel + reorder", shell:"ZiniPixelsShell", note:"Right panel shows Layers tab. Drag to reorder, eye icon to hide, lock icon." },
      { id:"step-canvas-export", label:"Export project", shell:"ZiniPixelsShell", note:"File menu > Export. Dialog with PNG/SVG/PDF options. Multi-select frames." },
    ],
  },
  {
    id:"collab-flow", label:"Collaborative editing", who:"designer", color:"T.cyan",
    gate:"Starter+",
    steps:[
      { id:"step-collab-open", label:"Open shared project", shell:"ZiniPixelsShell", note:"Project view shows avatars of online members. Click a file — see presence indicators." },
      { id:"step-collab-cursors", label:"See other users' cursors", shell:"ZiniPixelsShell", note:"Colored cursors with name labels. Selections highlighted in user colors. Name badges in viewport." },
      { id:"step-collab-conflict", label:"Conflict resolution", shell:"ZiniPixelsShell", note:"Yjs merge — brief toast 'Merged changes from Alice'. No data loss. Undo is per-user." },
    ],
  },
  {
    id:"comments-flow", label:"Comments & review", who:"designer", color:"T.teal",
    steps:[
      { id:"step-comment-create", label:"Add comment on element", shell:"ZiniPixelsShell", note:"Click comment tool on toolbar. Click canvas element. Comment input appears as thread starter." },
      { id:"step-comment-thread", label:"Threaded discussion", shell:"ZiniPixelsShell", note:"Comments panel open on right. Thread with replies. Mention @username. Resolve button." },
      { id:"step-comment-review", label:"Review mode", shell:"ZiniPixelsShell", note:"Toggle Review Mode in toolbar. Canvas enters review state. Badge pins on elements. Resolved threads dim." },
    ],
  },
  {
    id:"sharing-flow", label:"Share & manage access", who:"admin", color:"T.amber",
    steps:[
      { id:"step-sharing-open", label:"Open share dialog", shell:"ZiniPixelsShell", note:"Share button in topbar. Modal with 'Copy link' + permission dropdown." },
      { id:"step-sharing-invite", label:"Invite by email", shell:"ZiniPixelsShell", note:"Email input field. Role selector: Editor / Viewer / Commenter. Send invite. Toast confirmation." },
      { id:"step-sharing-members", label:"Manage members", shell:"ZiniPixelsShell", note:"Members list in project settings. Change roles, remove members, resend invites. Pending invites shown." },
    ],
  },
  {
    id:"ai-flow", label:"AI design assistant", who:"designer", color:"T.violet",
    gate:"Pro+",
    steps:[
      { id:"step-ai-open", label:"Open AI panel", shell:"ZiniPixelsShell", note:"AI button in toolbar toggles right panel. Chat interface: prompt input + history." },
      { id:"step-ai-generate", label:"Generate design from prompt", shell:"ZiniPixelsShell", note:"Type 'Create a landing page hero section'. AI generates shapes/text on canvas. Accept/regenerate/reject controls." },
      { id:"step-ai-suggest", label:"AI inline suggestions", shell:"ZiniPixelsShell", note:"Select a shape. AI suggests 'Add shadow', 'Change color', 'Resize'. Apply with one click." },
    ],
  },
  {
    id:"ds-flow", label:"Design system integration", who:"designer", color:"T.brand",
    gate:"Pro+",
    steps:[
      { id:"step-ds-open", label:"Open design system panel", shell:"ZiniPixelsShell", note:"DS icon in right panel tabs. Shows color tokens, typography, spacing, component library." },
      { id:"step-ds-apply-token", label:"Apply token to selection", shell:"ZiniPixelsShell", note:"Select a shape. Click token (e.g. 'Primary Blue'). Shape updates immediately. Token source shown." },
      { id:"step-ds-component", label:"Use component from library", shell:"ZiniPixelsShell", note:"Drag Button component from library onto canvas. Fully-styled with brand tokens. Editable." },
    ],
  },
  {
    id:"settings-flow", label:"Settings & billing", who:"admin", color:"T.amber",
    steps:[
      { id:"step-settings-open", label:"Open settings", shell:"ZiniPixelsShell", note:"Settings gear icon in topbar. Tabbed layout: General, Members, Billing, Profile." },
      { id:"step-settings-general", label:"Workspace settings", shell:"ZiniPixelsShell", note:"Workspace name, logo upload, default role for new members. Delete workspace (owner only)." },
      { id:"step-settings-billing", label:"Billing overview", shell:"ZiniPixelsShell", note:"Current plan, usage stats, payment method, upgrade/downgrade buttons. Invoice history." },
    ],
  },
  {
    id:"onboarding-flow", label:"Onboarding & plan selection", who:"designer", color:"T.green",
    steps:[
      { id:"step-onboarding-start", label:"Welcome screen", shell:"ZiniPixelsShell", note:"After sign-up. Welcome with product highlights. 'Get Started' CTA." },
      { id:"step-onboarding-plan", label:"Choose a plan", shell:"ZiniPixelsShell", note:"Plan cards: Free vs Pro vs Enterprise. Features comparison. Monthly/annual toggle." },
      { id:"step-onboarding-payment", label:"Set up payment", shell:"ZiniPixelsShell", note:"Card input via Stripe Elements. Billing address. Summary of plan. 'Start subscription'." },
      { id:"step-onboarding-complete", label:"Workspace created", shell:"ZiniPixelsShell", note:"Confirmation screen. 'Invite team' or 'Go to dashboard'. Onboarding checklist." },
    ],
  },
];

// ─── TECH STACK ─────────────────────────────────────────────────────────────────
const TECH_STACK = [
  ["Frontend","Next.js 14 + tldraw SDK + Yjs + Clerk Auth + Tailwind"],
  ["Backend","Next.js API Routes (serverless / Vercel Lambda) + Drizzle ORM"],
  ["Database","Neon PostgreSQL (serverless, free 3GB)"],
  ["Storage","Supabase Storage (S3-compatible, free 1GB)"],
  ["AI","LiteLLM + OpenRouter (Claude, GPT-4, Gemini)"],
  ["Collaboration","Yjs + y-websocket (tiny WS server on Railway ~$5/mo)"],
  ["Infra","Vercel (free) + Neon (free) + Supabase (free) + Railway (~$5/mo)"],
  ["Cost","~$50-100/mo at launch, ~$575/mo at 100 paid teams (92% margin)"],
];

// ─── CONFIGS ───────────────────────────────────────────────────────────────────
const CONFIGS = [
  { name:"Free", label:"Free", projects:"3", filesPerProject:"10", members:"3", aiTokens:"1K", storage:"50MB" },
  { name:"Pro", label:"Pro ($15/user/mo)", projects:"∞", filesPerProject:"∞", members:"25", aiTokens:"10K", storage:"1GB/user" },
  { name:"Enterprise", label:"Enterprise (custom)", projects:"∞", filesPerProject:"∞", members:"∞", aiTokens:"∞", storage:"Custom" },
];

// ─── SCREEN WIREFRAMES ─────────────────────────────────────────────────────────
function ScreenWF({ dark, viewport, screen, slim, role }) {
  const th = useTheme(dark);
  const mob = viewport === "mobile";
  const viewer = role === "viewer";

  const screens = {
    "dashboard-empty": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:mob?"20px 16px":"40px" }}>
            <div style={{ textAlign:"center", maxWidth:360 }}>
              <div style={{ fontSize:mob?48:64, marginBottom:16, opacity:0.3 }}>🎨</div>
              <div style={{ fontSize:mob?16:22, fontWeight:600, color:th.text, marginBottom:8 }}>No projects yet</div>
              <div style={{ fontSize:13, color:th.textSec, marginBottom:24, lineHeight:1.5 }}>
                Create your first project to start designing. Your projects will appear here.
              </div>
              <MockBtn label="Create Project" type="primary" w={mob?"100%":160} />
            </div>
          </div>
        </ZiniPixelsShell>
      </WF>
    ),

    "dashboard-populated": () => {
      const [viewMode, setViewMode] = useState("grid");
      const projects = [
        { name:"Marketing Site", files:12, members:3, time:"2h ago" },
        { name:"Mobile App Redesign", files:8, members:2, time:"Yesterday" },
        { name:"Dashboard UI Kit", files:24, members:5, time:"3d ago" },
        { name:"Landing Page 2026", files:4, members:1, time:"1w ago" },
        { name:"E-commerce Flow", files:16, members:4, time:"2w ago" },
        { name:"Brand Guidelines", files:6, members:2, time:"3w ago" },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", padding:mob?"10px":"16px 20px" }}>
              <Row mb={16} align="flex-start" wrap>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:mob?14:18, fontWeight:600, color:th.text }}>Projects</div>
                  <div style={{ fontSize:11, color:th.textSec, marginTop:2 }}>Acme Corp workspace</div>
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                  {!mob && (
                    <div style={{ display:"flex", gap:4, background:th.muted, borderRadius:6, padding:2 }}>
                      <div onClick={()=>setViewMode("grid")} style={{ padding:"4px 8px", borderRadius:4, fontSize:12, cursor:"default", background:viewMode==="grid"?th.surface:"transparent", color:th.text }}>⊞ Grid</div>
                      <div onClick={()=>setViewMode("list")} style={{ padding:"4px 8px", borderRadius:4, fontSize:12, cursor:"default", background:viewMode==="list"?th.surface:"transparent", color:th.text }}>☰ List</div>
                    </div>
                  )}
                  {!viewer && <MockBtn label="Create Project" type="primary" small />}
                </div>
              </Row>
              {!mob && (
                <div style={{ marginBottom:16, padding:"8px 12px", borderRadius:T.rsm, border:`1px solid ${th.border}`, background:th.surface, fontSize:12, color:th.textMuted, cursor:"default" }}>
                  🔍 Search projects...
                </div>
              )}
              {viewMode === "grid" ? (
                <div style={{ display:"grid", gridTemplateColumns:`repeat(${mob?1:3}, 1fr)`, gap:mob?10:16 }}>
                  {projects.map((p,i) => (
                    <div key={i} style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, overflow:"hidden", cursor:"default" }}>
                      <div style={{ height:mob?80:120, background:`linear-gradient(135deg, ${T.brandSoft}, ${T.cyanSoft})`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <div style={{ fontSize:mob?20:32, opacity:0.4 }}>🎨</div>
                      </div>
                      <div style={{ padding:mob?8:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                          <div style={{ fontWeight:600, fontSize:mob?11:13, color:th.text, flex:1 }}>{p.name}{viewer ? <> <MockBadge type="expiring" label="View only" /></> : null}</div>
                          {!viewer && <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>⋯</div>}
                        </div>
                        <div style={{ fontSize:10, color:th.textMuted, marginTop:4 }}>Edited {p.time}</div>
                        <div style={{ display:"flex", gap:12, marginTop:6, fontSize:10, color:th.textSec }}>
                          <span>📄 {p.files} files</span>
                          <span>👥 {p.members}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                  {projects.map((p,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 12px", background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rsm, cursor:"default" }}>
                      <div style={{ width:32, height:32, borderRadius:4, background:`linear-gradient(135deg, ${T.brandSoft}, ${T.cyanSoft})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, opacity:0.4 }}>🎨</div>
                      <div style={{ flex:1 }}><div style={{ fontWeight:500, fontSize:12, color:th.text }}>{p.name}</div></div>
                      <div style={{ fontSize:10, color:th.textSec, width:50, textAlign:"right" }}>{p.files} files</div>
                      <div style={{ fontSize:10, color:th.textSec, width:60, textAlign:"right" }}>{p.members} members</div>
                      <div style={{ fontSize:10, color:th.textMuted, width:60, textAlign:"right" }}>{p.time}</div>
                      {!viewer && <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>⋯</div>}
                    </div>
                  ))}
                </div>
              )}
              {mob && !viewer && (
                <div style={{ position:"fixed", bottom:70, right:16, width:48, height:48, borderRadius:"50%", background:T.brand, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, cursor:"default", boxShadow:"0 2px 12px rgba(0,0,0,0.2)" }}>+</div>
              )}
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "project-view-populated": () => {
      const files = ["Landing Page","Login Form","Dashboard","Settings Page","Navigation Bar"];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex" }}>
              <div style={{ width:mob?0:240, borderRight:`1px solid ${th.border}`, background:th.surface, flexShrink:0, display:mob?"none":"flex", flexDirection:"column" }}>
                <div style={{ padding:"14px 12px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:th.text }}>Marketing Site</div>
                    <div style={{ fontSize:10, color:th.textMuted, marginTop:2 }}>{files.length} files</div>
                  </div>
                  <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>⋯</div>
                </div>
                {!viewer && (
                  <div style={{ padding:"8px 12px", borderBottom:`1px solid ${th.border}` }}>
                    <MockBtn label="+ New File" type="outline" w="100%" small />
                  </div>
                )}
                <div style={{ flex:1, padding:"4px 0" }}>
                  {files.map((f,i) => (
                    <div key={f} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:5, background:i===0?th.muted:"transparent", cursor:"default" }}>
                      <span style={{ fontSize:14 }}>📄</span>
                      <span style={{ fontSize:11, color:th.text, flex:1 }}>{f}.frame</span>
                      <span style={{ fontSize:9, color:th.textMuted }}>2h ago</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:th.bg }}>
                <div style={{ textAlign:"center", maxWidth:320 }}>
                  <div style={{ fontSize:48, marginBottom:12, opacity:0.2 }}>🎨</div>
                  <div style={{ fontSize:14, color:th.textSec, marginBottom:4 }}>Design canvas — {files[0]}</div>
                  <div style={{ fontSize:11, color:th.textMuted }}>Coming in next phase</div>
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "create-project-modal": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:dark?"rgba(0,0,0,0.4)":"rgba(0,0,0,0.2)" }}>
            <div style={{ width:mob?"90%":420, background:th.elevated, borderRadius:T.rlg, border:`1px solid ${th.borderMd}`, boxShadow:"0 8px 32px rgba(0,0,0,0.3)" }}>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${th.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:13, fontWeight:600, color:th.text }}>Create Project</div>
                <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
              </div>
              <div style={{ padding:16, display:"flex", flexDirection:"column", gap:12 }}>
                <div>
                  <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Project name</div>
                  <MockField placeholder="e.g. Marketing Site" w="100%" />
                </div>
                <div>
                  <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Description (optional)</div>
                  <MockField placeholder="Brief description..." w="100%" multiline />
                </div>
                <div>
                  <div style={{ fontSize:10, color:th.textSec, marginBottom:6 }}>Template</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {["Blank","Mobile App","Landing Page","Dashboard","Wireframe"].map(t => (
                      <div key={t} style={{ padding:"4px 10px", borderRadius:6, fontSize:10, cursor:"default", background:t==="Blank"?T.brandSoft:"transparent", color:t==="Blank"?T.brand:th.textSec, border:`1px solid ${t==="Blank"?T.brandBorder:th.border}` }}>{t}</div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ padding:"12px 16px", borderTop:`1px solid ${th.border}`, display:"flex", justifyContent:"flex-end", gap:8 }}>
                <MockBtn label="Cancel" type="outline" small />
                <MockBtn label="Create" type="primary" small />
              </div>
            </div>
          </div>
        </ZiniPixelsShell>
      </WF>
    ),

    "rename-project-modal": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:dark?"rgba(0,0,0,0.4)":"rgba(0,0,0,0.2)" }}>
            <div style={{ width:mob?"90%":380, background:th.elevated, borderRadius:T.rlg, border:`1px solid ${th.borderMd}`, boxShadow:"0 8px 32px rgba(0,0,0,0.3)" }}>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${th.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:13, fontWeight:600, color:th.text }}>Rename Project</div>
                <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Project name</div>
                <MockField value="Marketing Site" w="100%" />
              </div>
              <div style={{ padding:"12px 16px", borderTop:`1px solid ${th.border}`, display:"flex", justifyContent:"flex-end", gap:8 }}>
                <MockBtn label="Cancel" type="outline" small />
                <MockBtn label="Save" type="primary" small />
              </div>
            </div>
          </div>
        </ZiniPixelsShell>
      </WF>
    ),

    "archive-project-modal": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:dark?"rgba(0,0,0,0.4)":"rgba(0,0,0,0.2)" }}>
            <div style={{ width:mob?"90%":380, background:th.elevated, borderRadius:T.rlg, border:`1px solid ${th.borderMd}`, boxShadow:"0 8px 32px rgba(0,0,0,0.3)" }}>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${th.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:13, fontWeight:600, color:th.text }}>Archive Project</div>
                <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ fontSize:11, color:th.textSec, lineHeight:1.5, marginBottom:4 }}>Archive <strong style={{ color:th.text }}>Marketing Site</strong>?</div>
                <div style={{ fontSize:10, color:T.amber, background:T.amberSoft, padding:"6px 10px", borderRadius:6 }}>Archived projects move to trash for 30 days. Team members will lose access.</div>
              </div>
              <div style={{ padding:"12px 16px", borderTop:`1px solid ${th.border}`, display:"flex", justifyContent:"flex-end", gap:8 }}>
                <MockBtn label="Cancel" type="outline" small />
                <MockBtn label="Archive" type="primary" small />
              </div>
            </div>
          </div>
        </ZiniPixelsShell>
      </WF>
    ),

    "delete-project-modal": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:dark?"rgba(0,0,0,0.4)":"rgba(0,0,0,0.2)" }}>
            <div style={{ width:mob?"90%":380, background:th.elevated, borderRadius:T.rlg, border:`1px solid ${th.borderMd}`, boxShadow:"0 8px 32px rgba(0,0,0,0.3)" }}>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${th.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:13, fontWeight:600, color:T.red }}>Delete Project</div>
                <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
              </div>
              <div style={{ padding:16, display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:11, color:th.textSec, lineHeight:1.5 }}>This will permanently delete <strong style={{ color:th.text }}>Marketing Site</strong> and all its files. This action cannot be undone.</div>
                <div>
                  <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Type <strong style={{ color:T.red }}>DELETE</strong> to confirm</div>
                  <MockField placeholder="Type DELETE" w="100%" />
                </div>
              </div>
              <div style={{ padding:"12px 16px", borderTop:`1px solid ${th.border}`, display:"flex", justifyContent:"flex-end", gap:8 }}>
                <MockBtn label="Cancel" type="outline" small />
                <MockBtn label="Delete" type="danger" small />
              </div>
            </div>
          </div>
        </ZiniPixelsShell>
      </WF>
    ),

    "viewer-dashboard": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <ZiniPixelsShell dark={dark} viewport={viewport} role="viewer">
          <div style={{ flex:1, display:"flex", flexDirection:"column", padding:mob?"10px":"16px 20px" }}>
            <Row mb={16}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:mob?14:18, fontWeight:600, color:th.text }}>Projects</div>
                <div style={{ fontSize:11, color:th.textSec, marginTop:2 }}>Acme Corp workspace · <MockBadge type="expiring" label="View only" /></div>
              </div>
            </Row>
            <div style={{ display:"grid", gridTemplateColumns:`repeat(${mob?1:3}, 1fr)`, gap:mob?10:16 }}>
              {["Marketing Site","Mobile App Redesign","Dashboard UI Kit"].map((p,i) => (
                <div key={i} style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, overflow:"hidden", cursor:"default", opacity:0.85 }}>
                  <div style={{ height:mob?80:120, background:`linear-gradient(135deg, ${T.brandSoft}, ${T.cyanSoft})`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <div style={{ fontSize:mob?20:32, opacity:0.4 }}>🎨</div>
                  </div>
                  <div style={{ padding:mob?8:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <div style={{ fontWeight:600, fontSize:mob?11:13, color:th.text }}>{p} <MockBadge type="expiring" label="View only" /></div>
                    </div>
                    <div style={{ fontSize:10, color:th.textMuted, marginTop:4 }}>Shared with you</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ZiniPixelsShell>
      </WF>
    ),

    "viewer-project-view": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <ZiniPixelsShell dark={dark} viewport={viewport} role="viewer">
          <div style={{ flex:1, display:"flex" }}>
            <div style={{ width:mob?0:240, borderRight:`1px solid ${th.border}`, background:th.surface, flexShrink:0, display:mob?"none":"flex", flexDirection:"column" }}>
              <div style={{ padding:"14px 12px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontSize:12, fontWeight:600, color:th.text }}>Marketing Site</div>
                  <div style={{ fontSize:10, color:th.textMuted, marginTop:2 }}>5 files · <MockBadge type="expiring" label="View only" /></div>
                </div>
              </div>
              <div style={{ flex:1, padding:"4px 0" }}>
                {["Landing Page","Login Form","Dashboard","Settings Page","Navigation Bar"].map((f,i) => (
                  <div key={f} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:5, background:i===0?th.muted:"transparent", cursor:"default" }}>
                    <span style={{ fontSize:14 }}>📄</span>
                    <span style={{ fontSize:11, color:th.text, flex:1 }}>{f}.frame</span>
                    <span style={{ fontSize:9, color:th.textMuted }}>2h ago</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:th.bg }}>
              <div style={{ textAlign:"center", maxWidth:320 }}>
                <div style={{ fontSize:48, marginBottom:12, opacity:0.2 }}>🎨</div>
                <div style={{ fontSize:13, color:th.textSec }}>Viewing <strong style={{ color:th.text }}>Landing Page.frame</strong></div>
                <div style={{ fontSize:10, color:th.textMuted, marginTop:4 }}>Read-only · Canvas interaction coming in next phase</div>
              </div>
            </div>
          </div>
        </ZiniPixelsShell>
      </WF>
    ),

    // ─── CANVAS SCREENS ──────────────────────────────────────────────────────────

    "canvas-empty": () => {
      const tools = ["Select","Draw","Text","Rectangle","Ellipse","Connector","Eraser"];
      const [activeTool] = useState("Select");
      const [fileTreeOpen] = useState(false);
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              {/* Toolbar */}
              <div style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderBottom:`1px solid ${th.border}`, background:th.surface, flexWrap:"nowrap", overflow:"auto" }}>
                {tools.map(t => (
                  <div key={t} style={{ padding:"4px 8px", borderRadius:5, fontSize:mob?9:10, cursor:"default", background:t===activeTool?T.brand:th.muted, color:t===activeTool?"#fff":th.textSec, whiteSpace:"nowrap", fontWeight:t===activeTool?500:400 }}>{t}</div>
                ))}
              </div>
              {/* Body */}
              <div style={{ flex:1, display:"flex" }}>
                {/* File tree (minimized) */}
                {!mob && (
                  <div style={{ width:36, borderRight:`1px solid ${th.border}`, background:th.surface, display:"flex", flexDirection:"column", alignItems:"center", paddingTop:6 }}>
                    <div style={{ width:24, height:24, borderRadius:4, background:th.muted, fontSize:10, lineHeight:"24px", textAlign:"center", color:th.textMuted, cursor:"default" }}>📄</div>
                    <div style={{ fontSize:8, color:th.textMuted, marginTop:2, writingMode:"vertical-lr", transform:"rotate(180deg)", lineHeight:1.2 }}>Landing Page</div>
                  </div>
                )}
                {/* Canvas area with grid */}
                <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <div style={{ fontSize:12, color:th.textMuted, opacity:0.5, userSelect:"none", cursor:"default" }}>Canvas ready</div>
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "canvas-shapes": () => {
      const tools = ["Select","Draw","Text","Rectangle","Ellipse","Connector","Eraser"];
      const [activeTool] = useState("Select");
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              {/* Toolbar */}
              <div style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderBottom:`1px solid ${th.border}`, background:th.surface, flexWrap:"nowrap", overflow:"auto" }}>
                {tools.map(t => (
                  <div key={t} style={{ padding:"4px 8px", borderRadius:5, fontSize:mob?9:10, cursor:"default", background:t===activeTool?T.brand:th.muted, color:t===activeTool?"#fff":th.textSec, whiteSpace:"nowrap", fontWeight:t===activeTool?500:400 }}>{t}</div>
                ))}
              </div>
              {/* Body */}
              <div style={{ flex:1, display:"flex" }}>
                {/* File tree minimized */}
                {!mob && (
                  <div style={{ width:36, borderRight:`1px solid ${th.border}`, background:th.surface, display:"flex", flexDirection:"column", alignItems:"center", paddingTop:6 }}>
                    <div style={{ width:24, height:24, borderRadius:4, background:th.muted, fontSize:10, lineHeight:"24px", textAlign:"center", color:th.textMuted, cursor:"default" }}>📄</div>
                    <div style={{ fontSize:8, color:th.textMuted, marginTop:2, writingMode:"vertical-lr", transform:"rotate(180deg)", lineHeight:1.2 }}>Landing Page</div>
                  </div>
                )}
                {/* Canvas */}
                <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", overflow:"hidden" }}>
                  {/* Blue rectangle with selection handles */}
                  <div style={{ position:"absolute", left:mob?20:120, top:mob?30:80, width:mob?100:160, height:mob?60:90, background:T.cyan, borderRadius:4, border:`2px solid ${T.brand}`, boxShadow:`0 0 0 2px ${T.brand}40` }}>
                    {/* Selection handles */}
                    <div style={{ position:"absolute", left:-4, top:-4, width:8, height:8, borderRadius:"50%", background:"#fff", border:`2px solid ${T.brand}`, cursor:"default" }} />
                    <div style={{ position:"absolute", right:-4, top:-4, width:8, height:8, borderRadius:"50%", background:"#fff", border:`2px solid ${T.brand}`, cursor:"default" }} />
                    <div style={{ position:"absolute", left:-4, bottom:-4, width:8, height:8, borderRadius:"50%", background:"#fff", border:`2px solid ${T.brand}`, cursor:"default" }} />
                    <div style={{ position:"absolute", right:-4, bottom:-4, width:8, height:8, borderRadius:"50%", background:"#fff", border:`2px solid ${T.brand}`, cursor:"default" }} />
                  </div>
                  {/* Green circle */}
                  <div style={{ position:"absolute", left:mob?140:320, top:mob?90:180, width:mob?60:80, height:mob?60:80, borderRadius:"50%", background:T.green, border:`2px solid ${T.green}`, opacity:0.8 }} />
                  {/* Text */}
                  <div style={{ position:"absolute", left:mob?120:280, top:mob?50:60, fontSize:mob?13:16, fontWeight:700, color:th.text, cursor:"default" }}>Homepage Title</div>
                  {/* Arrow connector */}
                  <svg style={{ position:"absolute", left:mob?120:280, top:mob?90:170, width:100, height:80, pointerEvents:"none" }} viewBox="0 0 100 80">
                    <line x1="0" y1="0" x2="60" y2="60" stroke={T.brand} strokeWidth={2} />
                    <polygon points="60,60 50,50 54,64" fill={T.brand} />
                  </svg>
                </div>
                {/* Properties hint */}
                {!mob && (
                  <div style={{ width:180, borderLeft:`1px solid ${th.border}`, background:th.surface, padding:12, fontSize:10, color:th.textMuted, display:"flex", flexDirection:"column", gap:6 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:th.textSec }}>Properties</div>
                    <div style={{ padding:"6px 8px", background:th.muted, borderRadius:4, fontSize:9, color:th.textMuted }}>X: 120 · Y: 80 · W: 160 · H: 90</div>
                    <div style={{ padding:"6px 8px", background:th.muted, borderRadius:4, fontSize:9, color:th.textMuted }}>Fill: {T.cyan}</div>
                    <div style={{ padding:"6px 8px", background:th.muted, borderRadius:4, fontSize:9, color:th.textMuted }}>Radius: 4px</div>
                    <div style={{ padding:"6px 8px", background:th.muted, borderRadius:4, fontSize:9, color:th.textMuted }}>Opacity: 100%</div>
                  </div>
                )}
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "canvas-layers": () => {
      const tools = ["Select","Draw","Text","Rectangle","Ellipse","Connector","Eraser"];
      const [activeTool] = useState("Select");
      const layers = [
        { name:"Layer 1", eye:true, lock:false, active:true },
        { name:"Layer 2", eye:false, lock:true, active:false },
        { name:"Background", eye:true, lock:false, active:false },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              {/* Toolbar */}
              <div style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderBottom:`1px solid ${th.border}`, background:th.surface, flexWrap:"nowrap", overflow:"auto" }}>
                {tools.map(t => (
                  <div key={t} style={{ padding:"4px 8px", borderRadius:5, fontSize:mob?9:10, cursor:"default", background:t===activeTool?T.brand:th.muted, color:t===activeTool?"#fff":th.textSec, whiteSpace:"nowrap" }}>{t}</div>
                ))}
              </div>
              <div style={{ flex:1, display:"flex" }}>
                {/* Canvas */}
                <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", overflow:"hidden" }}>
                  {/* Background layer - full rect */}
                  <div style={{ position:"absolute", left:"10%", top:"10%", width:"80%", height:"80%", border:`1px dashed ${th.borderMd}`, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:th.textMuted, opacity:0.4 }}>Background frame</div>
                  {/* Layer 1 shapes */}
                  <div style={{ position:"absolute", left:"30%", top:"35%", width:100, height:60, background:T.coral, borderRadius:4, opacity:0.9 }} />
                  <div style={{ position:"absolute", left:"55%", top:"50%", width:80, height:80, borderRadius:"50%", background:T.cyan, opacity:0.7 }} />
                  {/* Layer 2 shape (hidden eye) — shown with strikethrough style */}
                  <div style={{ position:"absolute", left:"45%", top:"25%", width:120, height:40, background:T.amber, borderRadius:4, opacity:0.3, filter:"grayscale(1)" }} />
                </div>
                {/* Layers panel */}
                {!mob && (
                  <div style={{ width:200, borderLeft:`1px solid ${th.border}`, background:th.surface, display:"flex", flexDirection:"column" }}>
                    <div style={{ padding:"10px 12px", borderBottom:`1px solid ${th.border}`, fontSize:11, fontWeight:600, color:th.text, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span>Layers</span>
                      <span style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>+</span>
                    </div>
                    {layers.map((l,i) => (
                      <div key={l.name} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", borderBottom:`1px solid ${th.border}`, background:l.active?T.brandSoft:"transparent", borderLeft:l.active?`3px solid ${T.brand}`:"3px solid transparent", cursor:"default" }}>
                        <span style={{ fontSize:12, color:l.eye?th.text:th.textMuted, cursor:"default" }}>{l.eye?"👁":"👁‍🗨"}</span>
                        <span style={{ fontSize:12, color:l.lock?th.textMuted:th.textSec, cursor:"default" }}>{l.lock?"🔒":"🔓"}</span>
                        <span style={{ flex:1, fontSize:11, color:l.active?T.brand:th.text, fontWeight:l.active?500:400 }}>{l.name}</span>
                        <span style={{ fontSize:10, color:th.textMuted, cursor:"default" }}>⋯</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "canvas-export": () => {
      const [format, setFormat] = useState("PNG");
      const [selectedOnly, setSelectedOnly] = useState(false);
      const formats = ["PNG","SVG","PDF"];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg, position:"relative" }}>
              {/* Dimmed canvas behind */}
              <div style={{ flex:1, background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", opacity:0.3, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <div style={{ fontSize:12, color:th.textMuted }}>Canvas dimmed</div>
              </div>
              {/* Export modal overlay */}
              <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:dark?"rgba(0,0,0,0.5)":"rgba(0,0,0,0.25)" }}>
                <div style={{ width:mob?"90%":380, background:th.elevated, borderRadius:T.rlg, border:`1px solid ${th.borderMd}`, boxShadow:"0 8px 32px rgba(0,0,0,0.3)" }}>
                  <div style={{ padding:"14px 16px", borderBottom:`1px solid ${th.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:th.text }}>Export</div>
                    <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
                  </div>
                  <div style={{ padding:16, display:"flex", flexDirection:"column", gap:14 }}>
                    <div>
                      <div style={{ fontSize:10, color:th.textSec, marginBottom:6 }}>Format</div>
                      <div style={{ display:"flex", gap:8 }}>
                        {formats.map(f => (
                          <div key={f} onClick={()=>setFormat(f)} style={{ flex:1, padding:"8px 0", textAlign:"center", borderRadius:6, fontSize:11, cursor:"default", background:f===format?T.brandSoft:"transparent", color:f===format?T.brand:th.textSec, border:`1px solid ${f===format?T.brandBorder:th.border}`, fontWeight:f===format?500:400 }}>
                            {f}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:14, height:14, borderRadius:3, border:`1px solid ${th.borderMd}`, background:selectedOnly?T.brand:"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#fff", cursor:"default" }}>{selectedOnly?"✓":""}</div>
                      <span style={{ fontSize:10, color:th.textSec, cursor:"default" }}>Selected frames only</span>
                    </div>
                  </div>
                  <div style={{ padding:"12px 16px", borderTop:`1px solid ${th.border}`, display:"flex", justifyContent:"flex-end", gap:8 }}>
                    <MockBtn label="Cancel" type="outline" small />
                    <MockBtn label="Export" type="primary" small />
                  </div>
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "collab-cursors": () => {
      const cursors = [
        { name:"Alice", color:T.red, x:"25%", y:"30%" },
        { name:"Bob", color:T.blue || "#3b82f6", x:"55%", y:"45%" },
        { name:"Charlie", color:T.green, x:"40%", y:"65%" },
      ];
      const selectionColors = ["rgba(239,68,68,0.15)","rgba(59,130,246,0.15)","rgba(34,197,94,0.15)"];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              {/* Topbar with avatars */}
              <div style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderBottom:`1px solid ${th.border}`, background:th.surface, justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                  {["Select","Draw","Text","Rectangle","Ellipse","Connector","Eraser"].map(t => (
                    <div key={t} style={{ padding:"4px 8px", borderRadius:5, fontSize:mob?9:10, cursor:"default", background:t==="Select"?T.brand:th.muted, color:t==="Select"?"#fff":th.textSec, whiteSpace:"nowrap" }}>{t}</div>
                  ))}
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  {cursors.map(c => (
                    <div key={c.name} style={{ position:"relative" }}>
                      <div style={{ width:24, height:24, borderRadius:"50%", background:c.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#fff", fontWeight:600, cursor:"default", border:`2px solid ${th.surface}` }}>{c.name[0]}</div>
                      <div style={{ position:"absolute", bottom:-2, right:-2, width:8, height:8, borderRadius:"50%", background:T.green, border:`1px solid ${th.surface}` }} />
                    </div>
                  ))}
                  <span style={{ fontSize:9, color:th.textMuted }}>3 online</span>
                </div>
              </div>
              {/* Canvas */}
              <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", overflow:"hidden" }}>
                {/* Alice's selection */}
                <div style={{ position:"absolute", left:"22%", top:"25%", width:140, height:90, borderRadius:4, border:`2px solid ${T.red}`, background:selectionColors[0] }}>
                  <div style={{ position:"absolute", left:-4, top:-4, width:8, height:8, borderRadius:"50%", background:T.red }} />
                  <div style={{ position:"absolute", right:-4, top:-4, width:8, height:8, borderRadius:"50%", background:T.red }} />
                  <div style={{ position:"absolute", left:-4, bottom:-4, width:8, height:8, borderRadius:"50%", background:T.red }} />
                  <div style={{ position:"absolute", right:-4, bottom:-4, width:8, height:8, borderRadius:"50%", background:T.red }} />
                </div>
                {/* Bob's selection */}
                <div style={{ position:"absolute", left:"52%", top:"40%", width:100, height:60, borderRadius:4, border:`2px solid ${T.blue || "#3b82f6"}`, background:selectionColors[1] }} />
                {/* Charlie's selection */}
                <div style={{ position:"absolute", left:"35%", top:"60%", width:120, height:80, borderRadius:4, border:`2px solid ${T.green}`, background:selectionColors[2] }} />
                {/* Cursor pointers */}
                {cursors.map((c, i) => (
                  <div key={c.name} style={{ position:"absolute", left:c.x, top:c.y, pointerEvents:"none" }}>
                    {/* Cursor SVG shape */}
                    <svg width="16" height="20" viewBox="0 0 16 20" style={{ position:"absolute", top:-4, left:-4 }}>
                      <polygon points="2,2 12,14 8,14 10,18 7,17 5,14 2,14" fill={c.color} />
                    </svg>
                    {/* Name label */}
                    <div style={{ position:"absolute", left:10, top:-6, fontSize:9, fontWeight:600, color:"#fff", background:c.color, padding:"1px 6px", borderRadius:4, whiteSpace:"nowrap", cursor:"default" }}>{c.name}</div>
                  </div>
                ))}
                {/* Canvas shapes */}
                <div style={{ position:"absolute", left:"25%", top:"28%", width:100, height:60, background:T.cyan, borderRadius:4, opacity:0.7 }} />
                <div style={{ position:"absolute", left:"55%", top:"43%", width:60, height:60, borderRadius:"50%", background:T.amber, opacity:0.7 }} />
                <div style={{ position:"absolute", left:"38%", top:"63%", width:100, height:40, background:T.violet, borderRadius:4, opacity:0.7 }} />
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── COMMENTS & REVIEW ──────────────────────────────────────────────────────

    "comment-create": () => {
      const tools = ["Select","Draw","Text","Rectangle","Ellipse","Connector","Eraser","💬 Comment"];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              <div style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderBottom:`1px solid ${th.border}`, background:th.surface, flexWrap:"nowrap", overflow:"auto" }}>
                {tools.map(t => (
                  <div key={t} style={{ padding:"4px 8px", borderRadius:5, fontSize:mob?9:10, cursor:"default", background:t===tools[7]?T.brand:th.muted, color:t===tools[7]?"#fff":th.textSec, whiteSpace:"nowrap", fontWeight:t===tools[7]?500:400 }}>{t}</div>
                ))}
              </div>
              <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", overflow:"hidden" }}>
                {/* Canvas shape with comment pin */}
                <div style={{ position:"absolute", left:mob?"15%":"25%", top:mob?"10%":"20%", width:mob?120:180, height:mob?70:100, background:T.cyan, borderRadius:4, opacity:0.6 }}>
                  <div style={{ position:"absolute", top:-8, right:-8, width:22, height:22, borderRadius:"50%", background:T.brand, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, cursor:"default", boxShadow:"0 2px 6px rgba(0,0,0,0.2)" }}>💬</div>
                </div>
                {/* Another shape */}
                <div style={{ position:"absolute", left:"50%", top:mob?"35%":"45%", width:mob?60:80, height:mob?60:80, borderRadius:"50%", background:T.amber, opacity:0.5 }} />
                {/* Pin line from comment bubble to shape */}
                <svg style={{ position:"absolute", left:mob?"15%":"25%", top:mob?"10%":"20%", width:200, height:200, pointerEvents:"none", overflow:"visible" }}>
                  <line x1={mob?120:180} y1={mob?70:100} x2={mob?120:180} y2={mob?100:140} stroke={T.brand} strokeWidth={1.5} strokeDasharray="3,2" />
                </svg>
                {/* Floating comment input bubble */}
                <div style={{ position:"absolute", left:mob?12:60, top:mob?140:180, width:mob?220:300, background:th.elevated, borderRadius:T.rmd, border:`1px solid ${th.borderMd}`, boxShadow:"0 4px 16px rgba(0,0,0,0.18)", padding:12, cursor:"default" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                    <div style={{ width:24, height:24, borderRadius:"50%", background:T.brandSoft, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:T.brand, fontWeight:600, flexShrink:0, cursor:"default" }}>U</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:10, fontWeight:600, color:th.text, marginBottom:2 }}>You</div>
                      <div style={{ padding:"6px 10px", background:th.muted, borderRadius:T.rsm, fontSize:11, color:th.textMuted, cursor:"default" }}>Add a comment...</div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:8 }}>
                        <MockBtn label="Post" type="primary" small />
                        <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "comment-thread": () => {
      const threads = [
        { name:"Alice", time:"2h ago", color:T.red, text:"This button needs more padding. It feels cramped on mobile." },
        { name:"Bob", time:"1h ago", color:T.amber, text:"Agreed. Let's increase to 16px horizontal." },
        { name:"Charlie", time:"30m ago", color:T.green, text:"Updated. Check the new version." },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", background:th.bg }}>
              {/* Canvas area */}
              <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", overflow:"hidden" }}>
                {/* Canvas shapes with pin indicators */}
                <div style={{ position:"absolute", left:"15%", top:"25%", width:200, height:90, background:T.cyan, borderRadius:4, opacity:0.5, border:`2px dashed ${T.brand}` }}>
                  <div style={{ position:"absolute", top:-8, right:-8, width:22, height:22, borderRadius:"50%", background:T.red, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#fff", cursor:"default" }}>1</div>
                </div>
                <div style={{ position:"absolute", left:"55%", top:"45%", width:140, height:60, background:T.amber, borderRadius:8, opacity:0.5 }}>
                  <div style={{ position:"absolute", top:-8, left:-8, width:22, height:22, borderRadius:"50%", background:T.amber, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#fff", cursor:"default" }}>2</div>
                </div>
                <div style={{ position:"absolute", left:"35%", top:"65%", width:100, height:100, borderRadius:"50%", background:T.violet, opacity:0.4 }} />
              </div>
              {/* Comments panel */}
              {!mob && (
                <div style={{ width:320, borderLeft:`1px solid ${th.border}`, background:th.surface, display:"flex", flexDirection:"column" }}>
                  <div style={{ padding:"12px 14px", borderBottom:`1px solid ${th.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:th.text }}>Comments</div>
                    <div style={{ display:"flex", gap:6 }}>
                      <span style={{ fontSize:11, color:th.textMuted, cursor:"default" }}>✕</span>
                    </div>
                  </div>
                  {/* Thread */}
                  <div style={{ flex:1, overflow:"auto", padding:10, display:"flex", flexDirection:"column", gap:8 }}>
                    {threads.map(t => (
                      <div key={t.name} style={{ display:"flex", gap:8, alignItems:"flex-start", padding:8, background:t===threads[0]?T.brandSoft:"transparent", borderRadius:T.rsm, border:t===threads[0]?`1px solid ${T.brandBorder}`:"none" }}>
                        <div style={{ width:22, height:22, borderRadius:"50%", background:t.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#fff", fontWeight:600, flexShrink:0, cursor:"default" }}>{t.name[0]}</div>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                            <span style={{ fontSize:10, fontWeight:600, color:th.text }}>{t.name}</span>
                            <span style={{ fontSize:8, color:th.textMuted }}>{t.time}</span>
                          </div>
                          <div style={{ fontSize:10, color:th.textSec, lineHeight:1.4 }}>{t.text}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Reply input */}
                  <div style={{ padding:"8px 10px", borderTop:`1px solid ${th.border}`, display:"flex", gap:6, alignItems:"center" }}>
                    <MockField placeholder="Reply..." w="100%" />
                    <MockBtn label="Reply" type="primary" small />
                  </div>
                  {/* Resolve */}
                  <div style={{ padding:"6px 10px", borderTop:`1px solid ${th.border}`, display:"flex", justifyContent:"center" }}>
                    <div style={{ fontSize:10, color:T.green, cursor:"default", padding:"4px 12px" }}>✓ Resolve thread</div>
                  </div>
                </div>
              )}
              {mob && (
                <div style={{ position:"absolute", bottom:0, left:0, right:0, background:th.surface, borderTop:`1px solid ${th.border}`, padding:8 }}>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    <MockField placeholder="Reply..." w="100%" />
                    <MockBtn label="Reply" type="primary" small />
                  </div>
                </div>
              )}
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "comment-review-mode": () => {
      const pins = [
        { id:1, label:"Button padding", color:T.red, resolved:false, left:"20%", top:"20%" },
        { id:2, label:"Header alignment", color:T.brand, resolved:false, left:"55%", top:"30%" },
        { id:3, label:"Color contrast", color:T.green, resolved:false, left:"35%", top:"55%" },
        { id:4, label:"Font size", color:th.textMuted, resolved:true, left:"65%", top:"60%" },
        { id:5, label:"Spacing", color:th.textMuted, resolved:true, left:"40%", top:"75%" },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              {/* Topbar with review mode badge */}
              <div style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderBottom:`1px solid ${th.border}`, background:th.surface, justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  {["Select","Draw","Text","Rectangle","Ellipse","Connector","Eraser"].map(t => (
                    <div key={t} style={{ padding:"4px 8px", borderRadius:5, fontSize:mob?9:10, cursor:"default", background:t==="Select"?T.brand:th.muted, color:t==="Select"?"#fff":th.textSec, whiteSpace:"nowrap" }}>{t}</div>
                  ))}
                  <div style={{ padding:"2px 8px", borderRadius:8, fontSize:9, fontWeight:600, background:T.amber, color:"#fff", cursor:"default", marginLeft:4 }}>🔍 Review Mode</div>
                </div>
              </div>
              <div style={{ flex:1, display:"flex" }}>
                {/* Canvas */}
                <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", overflow:"hidden" }}>
                  {/* Canvas shapes */}
                  <div style={{ position:"absolute", left:"20%", top:"22%", width:160, height:80, background:T.cyan, borderRadius:4, opacity:0.5, border:pins[0].resolved?`1px solid ${th.borderMd}`:`2px solid ${pins[0].color}` }} />
                  <div style={{ position:"absolute", left:"55%", top:"32%", width:200, height:50, background:T.amber, borderRadius:4, opacity:0.5, border:pins[1].resolved?`1px solid ${th.borderMd}`:`2px solid ${pins[1].color}` }} />
                  <div style={{ position:"absolute", left:"35%", top:"57%", width:120, height:120, borderRadius:"50%", background:T.violet, opacity:0.4, border:pins[2].resolved?`1px solid ${th.borderMd}`:`2px solid ${pins[2].color}` }} />
                  <div style={{ position:"absolute", left:"65%", top:"62%", width:100, height:40, background:T.teal, borderRadius:4, opacity:0.4 }} />
                  <div style={{ position:"absolute", left:"40%", top:"77%", width:140, height:30, background:T.coral, borderRadius:4, opacity:0.4 }} />
                  {/* Annotation pins */}
                  {pins.map(p => (
                    <div key={p.id} style={{ position:"absolute", left:p.left, top:p.top, display:"flex", alignItems:"center", gap:4, cursor:"default" }}>
                      <div style={{ width:p.resolved?18:22, height:p.resolved?18:22, borderRadius:"50%", background:p.resolved?th.textMuted:p.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:p.resolved?8:10, color:"#fff", fontWeight:600, opacity:p.resolved?0.5:1, boxShadow:p.resolved?"none":`0 0 0 2px ${p.color}40` }}>{p.resolved?"✓":p.id}</div>
                      {!mob && !p.resolved && (
                        <div style={{ fontSize:9, color:th.textSec, background:th.surface, padding:"1px 6px", borderRadius:4, border:`1px solid ${th.border}`, whiteSpace:"nowrap", cursor:"default" }}>{p.label}</div>
                      )}
                    </div>
                  ))}
                </div>
                {/* Review summary panel */}
                {!mob && (
                  <div style={{ width:260, borderLeft:`1px solid ${th.border}`, background:th.surface, display:"flex", flexDirection:"column" }}>
                    <div style={{ padding:"12px 14px", borderBottom:`1px solid ${th.border}`, fontSize:12, fontWeight:600, color:th.text }}>Review Summary</div>
                    <div style={{ padding:"10px 14px", display:"flex", flexDirection:"column", gap:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:th.text }}>
                        <span>🔴 Unresolved</span>
                        <span style={{ fontWeight:600, color:T.red }}>3</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:th.text }}>
                        <span>✅ Resolved</span>
                        <span style={{ fontWeight:600, color:T.green }}>2</span>
                      </div>
                      <div style={{ height:1, background:th.border, margin:"4px 0" }} />
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:th.text }}>
                        <span>Total</span>
                        <span style={{ fontWeight:600 }}>5</span>
                      </div>
                    </div>
                    <div style={{ padding:"4px 14px", display:"flex", flexDirection:"column", gap:4 }}>
                      {pins.filter(p => !p.resolved).map(p => (
                        <div key={p.id} style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 6px", background:th.muted, borderRadius:4, fontSize:9, color:th.textSec, cursor:"default" }}>
                          <div style={{ width:6, height:6, borderRadius:"50%", background:p.color }} />
                          {p.label}
                        </div>
                      ))}
                    </div>
                    <div style={{ padding:"12px 14px", borderTop:`1px solid ${th.border}`, marginTop:"auto" }}>
                      <MockBtn label="Exit Review" type="outline" w="100%" small />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── ACCESS MANAGEMENT & SHARING ───────────────────────────────────────────

    "sharing-modal": () => {
      const [roleFilter, setRoleFilter] = useState("edit");
      const members = [
        { name:"Alice", email:"alice@acme.com", role:"Editor", color:T.red },
        { name:"Bob", email:"bob@acme.com", role:"Viewer", color:T.amber },
        { name:"Charlie", email:"charlie@acme.com", role:"Commenter", color:T.green },
        { name:"You", email:"you@acme.com", role:"Owner", color:T.brand, self:true },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:dark?"rgba(0,0,0,0.5)":"rgba(0,0,0,0.25)", position:"relative" }}>
              {/* Dimmed canvas hint */}
              {!mob && <div style={{ position:"absolute", inset:0, background:dark?"#0d1117":"#e8edf2", opacity:0.15 }} />}
              {/* Modal */}
              <div style={{ width:mob?"95%":480, maxHeight:mob?"95%":"auto", background:th.elevated, borderRadius:T.rlg, border:`1px solid ${th.borderMd}`, boxShadow:"0 8px 32px rgba(0,0,0,0.3)", display:"flex", flexDirection:"column", position:"relative", zIndex:1 }}>
                {/* Header */}
                <div style={{ padding:"14px 16px", borderBottom:`1px solid ${th.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ fontSize:13, fontWeight:600, color:th.text }}>Share project</div>
                  <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
                </div>
                {/* Body */}
                <div style={{ padding:16, display:"flex", flexDirection:"column", gap:16, overflow:"auto" }}>
                  {/* Copy link */}
                  <div>
                    <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:6 }}>Share link</div>
                    <div style={{ display:"flex", gap:6 }}>
                      <MockField value="https://app.zinipixels.com/share/abc123" w="100%" />
                      <MockBtn label="Copy" type="outline" small />
                    </div>
                  </div>
                  {/* Invite by email */}
                  <div>
                    <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:6 }}>Invite by email</div>
                    <div style={{ display:"flex", gap:6, flexDirection:mob?"column":"row" }}>
                      <MockField placeholder="colleague@example.com" w={mob?"100%":"60%"} />
                      <div style={{ display:"flex", gap:4, width:mob?"100%":"40%" }}>
                        <div style={{ flex:1, padding:"6px 8px", borderRadius:T.rsm, border:`1px solid ${th.border}`, background:th.muted, fontSize:10, color:th.text, display:"flex", alignItems:"center", cursor:"default" }}>Editor ▾</div>
                        <MockBtn label="Send" type="primary" small />
                      </div>
                    </div>
                  </div>
                  {/* Current members */}
                  <div>
                    <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:6 }}>Current members</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      {members.map(m => (
                        <div key={m.email} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", background:m.self?T.brandSoft:"transparent", borderRadius:T.rsm, border:m.self?`1px solid ${T.brandBorder}`:"none", cursor:"default" }}>
                          <div style={{ width:24, height:24, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#fff", fontWeight:600, flexShrink:0 }}>{m.name[0]}</div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:10, fontWeight:600, color:th.text }}>{m.name}{m.self?" (you)":""}</div>
                            <div style={{ fontSize:8, color:th.textMuted }}>{m.email}</div>
                          </div>
                          <div style={{ padding:"3px 6px", borderRadius:4, fontSize:8, cursor:"default", background:th.muted, border:`1px solid ${th.border}`, color:th.textSec, whiteSpace:"nowrap" }}>{m.role} ▾</div>
                          {!m.self && <div style={{ fontSize:12, color:th.textMuted, cursor:"default", padding:"0 4px" }}>✕</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "members-list": () => {
      const [page] = useState(1);
      const members = [
        { name:"Alice", email:"alice@acme.com", role:"Editor", status:"active", color:T.red, initials:"A"},
        { name:"Bob", email:"bob@acme.com", role:"Viewer", status:"active", color:T.amber, initials:"B"},
        { name:"Charlie", email:"charlie@acme.com", role:"Commenter", status:"active", color:T.green, initials:"C"},
        { name:"Diana", email:"diana@acme.com", role:"Editor", status:"active", color:T.violet, initials:"D"},
        { name:"Eve", email:"eve@acme.com", role:"Viewer", status:"active", color:T.coral, initials:"E"},
        { name:"Frank", email:"frank@acme.com", role:"Editor", status:"pending", color:T.amber, initials:"F"},
      ];
      const roles = ["Editor","Viewer","Commenter"];
      const roleColors = { Editor:T.brand, Viewer:T.amber, Commenter:T.teal };
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", padding:mob?"10px 12px":"16px 24px" }}>
              {/* Header */}
              <Row mb={mob?10:16} align="flex-start" wrap>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:mob?14:18, fontWeight:600, color:th.text }}>Members</div>
                  <div style={{ fontSize:11, color:th.textSec, marginTop:2 }}>12 members</div>
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  {!mob && <MockField placeholder="🔍 Search members..." w={180} />}
                  <MockBtn label="+ Invite" type="primary" small />
                </div>
              </Row>
              {mob && <div style={{ marginBottom:10 }}><MockField placeholder="🔍 Search members..." w="100%" /></div>}
              {/* Table header */}
              {!mob && (
                <div style={{ display:"grid", gridTemplateColumns:"28px 1fr 180px 100px 90px 120px", gap:8, padding:"6px 12px", fontSize:9, fontWeight:600, color:th.textMuted, borderBottom:`1px solid ${th.border}`, marginBottom:2 }}>
                  <span /><span>Name</span><span>Email</span><span>Role</span><span>Status</span><span>Actions</span>
                </div>
              )}
              {/* Members rows */}
              <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                {members.map((m,i) => (
                  <div key={m.email} style={{ display:"grid", gridTemplateColumns:mob?"1fr":"28px 1fr 180px 100px 90px 120px", gap:8, padding:mob?"8px 0":"8px 12px",background:i%2===0?th.muted:"transparent",borderRadius:T.rsm,alignItems:"center", cursor:"default" }}>
                    {!mob && (
                      <div style={{ width:22, height:22, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, color:"#fff", fontWeight:600 }}>{m.initials}</div>
                    )}
                    <div>
                      {mob && (
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                          <div style={{ width:18, height:18, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:7, color:"#fff", fontWeight:600 }}>{m.initials}</div>
                          <span style={{ fontSize:11, fontWeight:600, color:th.text }}>{m.name}</span>
                        </div>
                      )}
                      {!mob && <span style={{ fontSize:11, color:th.text }}>{m.name}</span>}
                      <span style={{ fontSize:9, color:th.textMuted, display:"block" }}>{m.email}</span>
                    </div>
                    <div>
                      <MockBadge type="neutral" label={m.role} style={{ background:roleColors[m.role]+"20", color:roleColors[m.role] }} />
                    </div>
                    <div>
                      <span style={{ fontSize:9, padding:"2px 6px", borderRadius:4, background:m.status==="active"?T.greenSoft:T.amberSoft, color:m.status==="active"?T.green:T.amber, fontWeight:500 }}>
                        {m.status==="active"?"● Active":mob?"⌛ Pending":"⌛ Pending"}
                      </span>
                    </div>
                    <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                      <div style={{ padding:"2px 6px", borderRadius:4, fontSize:8, border:`1px solid ${th.border}`, color:th.textSec, cursor:"default", whiteSpace:"nowrap" }}>Role ▾</div>
                      <div style={{ fontSize:11, color:th.textMuted, cursor:"default" }}>✕</div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Pagination */}
              <div style={{ marginTop:"auto", paddingTop:16, display:"flex", justifyContent:"space-between", alignItems:"center", borderTop:`1px solid ${th.border}` }}>
                <div style={{ fontSize:9, color:th.textMuted }}>Showing 1-6 of 12</div>
                <div style={{ display:"flex", gap:4 }}>
                  <div style={{ padding:"4px 8px", borderRadius:4, fontSize:9, background:th.muted, color:th.textMuted, cursor:"default" }}>‹</div>
                  {[1,2].map(p => (
                    <div key={p} style={{ padding:"4px 8px", borderRadius:4, fontSize:9, background:p===page?T.brandSoft:"transparent", color:p===page?T.brand:th.textMuted, cursor:"default" }}>{p}</div>
                  ))}
                  <div style={{ padding:"4px 8px", borderRadius:4, fontSize:9, background:th.muted, color:th.textMuted, cursor:"default" }}>›</div>
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "invite-pending": () => {
      const members = [
        { name:"Alice", email:"alice@acme.com", role:"Editor", status:"active", color:T.red },
        { name:"Bob", email:"bob@acme.com", role:"Viewer", status:"active", color:T.amber },
        { name:"Diana", email:"diana@acme.com", role:"Editor", status:"active", color:T.violet },
        { name:"Frank", email:"frank@acme.com", role:"Editor", status:"pending", color:T.amber },
        { name:"invited@example.com", email:"invited@example.com", role:"Commenter", status:"invited", color:th.textMuted },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", padding:mob?"10px 12px":"16px 24px" }}>
              {/* Toast strip */}
              <div style={{ padding:"8px 12px", borderRadius:T.rsm, background:T.amberSoft, border:`1px solid ${T.amber}40`, display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, cursor:"default" }}>
                <div style={{ fontSize:10, color:T.amber, fontWeight:500 }}>✉️ Invite sent to alice@example.com · <span style={{ textDecoration:"underline", cursor:"default" }}>Resend</span></div>
                <div style={{ fontSize:12, color:T.amber, cursor:"default" }}>✕</div>
              </div>
              {/* Header */}
              <Row mb={mob?10:16} align="flex-start" wrap>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:mob?14:18, fontWeight:600, color:th.text }}>Members</div>
                  <div style={{ fontSize:11, color:th.textSec, marginTop:2 }}>7 members · 1 pending</div>
                </div>
                <MockBtn label="+ Invite" type="primary" small />
              </Row>
              {/* Members */}
              <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                {members.map((m,i) => {
                  const invited = m.status === "invited";
                  return (
                    <div key={m.email} style={{ display:"grid", gridTemplateColumns:mob?"1fr":"28px 1fr 180px 100px 120px", gap:8, padding:mob?"8px 0":"8px 12px", background:invited?T.amberSoft:i%2===0?th.muted:"transparent", borderRadius:T.rsm, border:invited?`1px solid ${T.amber}40`:"none", alignItems:"center", cursor:"default" }}>
                      {!mob && (
                        <div style={{ width:22, height:22, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, color:"#fff", fontWeight:600 }}>{m.name[0]}</div>
                      )}
                      <div>
                        {mob && (
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                            <div style={{ width:18, height:18, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:7, color:"#fff", fontWeight:600 }}>{m.name[0]}</div>
                            <span style={{ fontSize:11, fontWeight:600, color:th.text }}>{m.name}</span>
                          </div>
                        )}
                        {!mob && <span style={{ fontSize:11, color:th.text }}>{m.name}</span>}
                        <span style={{ fontSize:9, color:th.textMuted, display:"block" }}>{m.email}</span>
                      </div>
                      <div style={{ fontSize:9, padding:"2px 6px", borderRadius:4, background:T.brandSoft, color:T.brand, width:"fit-content" }}>{m.role}</div>
                      <div>
                        {m.status === "invited" ? (
                          <span style={{ fontSize:9, padding:"2px 6px", borderRadius:4, background:T.amberSoft, color:T.amber, fontWeight:500 }}>⌛ Pending</span>
                        ) : (
                          <span style={{ fontSize:9, padding:"2px 6px", borderRadius:4, background:T.greenSoft, color:T.green, fontWeight:500 }}>● Active</span>
                        )}
                      </div>
                      <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                        {invited ? (
                          <>
                            <div style={{ padding:"2px 6px", borderRadius:4, fontSize:8, background:T.brandSoft, color:T.brand, cursor:"default" }}>Resend</div>
                            <div style={{ padding:"2px 6px", borderRadius:4, fontSize:8, background:T.redSoft, color:T.red, cursor:"default" }}>Revoke</div>
                          </>
                        ) : (
                          <>
                            <div style={{ padding:"2px 6px", borderRadius:4, fontSize:8, border:`1px solid ${th.border}`, color:th.textMuted, cursor:"default", whiteSpace:"nowrap" }}>Role ▾</div>
                            <div style={{ fontSize:11, color:th.textMuted, cursor:"default" }}>✕</div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "access-denied": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:th.bg, minHeight:mob?"100%":"100%", padding:20 }}>
          <div style={{ textAlign:"center", maxWidth:mob?"100%":380 }}>
            <div style={{ fontSize:48, marginBottom:16, cursor:"default" }}>🔒</div>
            <div style={{ fontSize:mob?18:22, fontWeight:700, color:th.text, marginBottom:8 }}>Access Denied</div>
            <div style={{ fontSize:13, color:th.textSec, marginBottom:24, lineHeight:1.5 }}>
              You don't have permission to view this project. Contact the project owner to request access.
            </div>
            <MockBtn label="Request Access" type="primary" w={mob?"100%":200} />
            <div style={{ marginTop:12 }}>
              <span style={{ fontSize:11, color:T.brand, cursor:"default", textDecoration:"underline" }}>Go back to dashboard</span>
            </div>
          </div>
        </div>
      </WF>
    ),

    // ─── AI CONNECTORS — AI Panel ─────────────────────────────────────────────

    "ai-panel": () => {
      const messages = [
        { role:"user", text:"Create a hero section for my landing page" },
        { role:"ai", text:"Generating hero section...", status:"loading" },
        { role:"ai", text:"Here's a hero with gradient background, headline, subtitle, and CTA button.", status:"done", thumbnail:true },
      ];
      const suggestions = ["Add a gradient background","Create a button component","Generate color palette"];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", background:th.bg }}>
              {/* Canvas area */}
              <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                {/* Sample canvas shapes */}
                <div style={{ position:"absolute", left:mob?"8%":"15%", top:mob?"8%":"15%", width:mob?"60%":"55%", height:mob?"50%":"55%", background:`linear-gradient(135deg, ${T.brand}20, ${T.cyan}15)`, borderRadius:12, border:`1px solid ${T.brandBorder}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8, padding:20 }}>
                  <div style={{ width:"70%", height:14, borderRadius:4, background:th.text, opacity:0.2 }} />
                  <div style={{ width:"50%", height:8, borderRadius:4, background:th.text, opacity:0.12 }} />
                  <div style={{ width:"40%", height:34, borderRadius:8, background:T.brand, opacity:0.3, marginTop:4 }} />
                </div>
              </div>
              {/* AI Panel — right sidebar */}
              <div style={{ width:mob?0:300, borderLeft:`1px solid ${th.border}`, background:th.surface, flexShrink:0, display:"flex", flexDirection:"column", position:"relative" }}>
                {/* Panel header */}
                <div style={{ padding:"10px 14px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:14 }}>🤖</span>
                    <span style={{ fontSize:12, fontWeight:600, color:th.text }}>AI Assistant</span>
                  </div>
                  <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
                </div>
                {/* Chat messages */}
                <div style={{ flex:1, overflow:"auto", padding:"8px 10px", display:"flex", flexDirection:"column", gap:8 }}>
                  {messages.map((m,i) => (
                    <div key={i} style={{ display:"flex", gap:6, alignItems:"flex-start", justifyContent:m.role==="user"?"flex-end":"flex-start" }}>
                      {m.role==="ai" && <div style={{ width:22, height:22, borderRadius:"50%", background:T.brandSoft, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, flexShrink:0 }}>🤖</div>}
                      <div style={{ maxWidth:"80%", padding:"6px 10px", borderRadius:T.rmd, fontSize:10, lineHeight:1.4, background:m.role==="user"?T.brand:th.muted, color:m.role==="user"?"#fff":th.text, border:m.role==="user"?"none":`1px solid ${th.border}` }}>
                        {m.text}
                        {m.thumbnail && (
                          <div style={{ marginTop:6, padding:8, background:th.surface, borderRadius:T.rsm, border:`1px solid ${th.border}`, display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:32, height:24, borderRadius:3, background:`linear-gradient(135deg, ${T.brand}40, ${T.cyan}30)`, flexShrink:0 }} />
                            <div style={{ fontSize:8, color:th.textMuted }}>Hero section preview</div>
                          </div>
                        )}
                        {m.status==="loading" && <span style={{ display:"inline-block", marginLeft:4, animation:"none" }}>⏳</span>}
                      </div>
                      {m.role==="user" && <div style={{ width:20, height:20, borderRadius:"50%", background:T.brand, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, color:"#fff", fontWeight:600, flexShrink:0 }}>U</div>}
                    </div>
                  ))}
                </div>
                {/* Suggested prompts */}
                <div style={{ padding:"6px 10px", borderTop:`1px solid ${th.border}`, display:"flex", gap:4, flexWrap:"wrap" }}>
                  {suggestions.map(s => (
                    <div key={s} style={{ padding:"3px 8px", borderRadius:10, fontSize:8, background:T.brandSoft, color:T.brand, border:`1px solid ${T.brandBorder}`, cursor:"default", whiteSpace:"nowrap" }}>{s}</div>
                  ))}
                </div>
                {/* Prompt input */}
                <div style={{ padding:"8px 10px", borderTop:`1px solid ${th.border}`, display:"flex", gap:6, alignItems:"center" }}>
                  <div style={{ flex:1, height:32, borderRadius:T.rsm, border:`1px solid ${th.border}`, background:th.muted, display:"flex", alignItems:"center", padding:"0 10px", fontSize:10, color:th.textMuted, cursor:"default" }}>Ask AI...</div>
                  <div style={{ width:28, height:28, borderRadius:T.rsm, background:T.brand, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:"#fff", cursor:"default" }}>➤</div>
                </div>
                {/* Token usage */}
                <div style={{ padding:"4px 10px", borderTop:`1px solid ${th.border}`, fontSize:8, color:th.textMuted, textAlign:"center", cursor:"default" }}>
                  245/10K tokens used <span style={{ color:T.amber }}>▰▰▰▰▰▰▰▰▰▰▰▰▱▱▱▱▱▱▱▱</span>
                </div>
              </div>
              {/* Mobile — bottom sheet */}
              {mob && (
                <div style={{ position:"absolute", bottom:0, left:0, right:0, background:th.surface, borderTop:`1px solid ${th.border}`, padding:"6px 10px", zIndex:10 }}>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    <div style={{ flex:1, height:30, borderRadius:T.rsm, border:`1px solid ${th.border}`, background:th.muted, display:"flex", alignItems:"center", padding:"0 8px", fontSize:9, color:th.textMuted, cursor:"default" }}>Ask AI...</div>
                    <div style={{ width:26, height:26, borderRadius:T.rsm, background:T.brand, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#fff", cursor:"default" }}>➤</div>
                  </div>
                </div>
              )}
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── AI CONNECTORS — AI Generate Modal ─────────────────────────────────────

    "ai-generate": () => {
      const [variations, setVariations] = useState(2);
      const [stylePreset, setStylePreset] = useState("modern");
      const presets = [
        { key:"modern", label:"Modern" },
        { key:"minimal", label:"Minimal" },
        { key:"bold", label:"Bold" },
        { key:"playful", label:"Playful" },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:dark?"rgba(0,0,0,0.5)":"rgba(0,0,0,0.25)", position:"relative" }}>
              {!mob && <div style={{ position:"absolute", inset:0, background:dark?"#0d1117":"#e8edf2", opacity:0.15 }} />}
              {/* Modal */}
              <div style={{ width:mob?"92%":480, maxHeight:mob?"95%":"auto", background:th.elevated, borderRadius:T.rlg, border:`1px solid ${th.borderMd}`, boxShadow:"0 8px 32px rgba(0,0,0,0.3)", display:"flex", flexDirection:"column", position:"relative", zIndex:1 }}>
                {/* Header */}
                <div style={{ padding:"14px 16px", borderBottom:`1px solid ${th.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:14 }}>🤖</span>
                    <span style={{ fontSize:13, fontWeight:600, color:th.text }}>Generate with AI</span>
                  </div>
                  <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
                </div>
                {/* Body */}
                <div style={{ padding:16, display:"flex", flexDirection:"column", gap:14, overflow:"auto" }}>
                  {/* Prompt */}
                  <div>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Prompt</div>
                    <div style={{ padding:"10px 12px", borderRadius:T.rsm, border:`1px solid ${th.border}`, background:th.muted, minHeight:72, fontSize:11, color:th.textMuted, cursor:"default", lineHeight:1.5 }}>Describe what you want to create...</div>
                  </div>
                  {/* Style preset */}
                  <div>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:6 }}>Style preset</div>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {presets.map(p => (
                        <div key={p.key} onClick={()=>setStylePreset(p.key)} style={{ padding:"5px 12px", borderRadius:8, fontSize:10, cursor:"default", background:stylePreset===p.key?T.brand:th.muted, color:stylePreset===p.key?"#fff":th.textSec, border:`1px solid ${stylePreset===p.key?T.brand:th.border}`, fontWeight:stylePreset===p.key?500:400 }}>{p.label}</div>
                      ))}
                    </div>
                  </div>
                  {/* Variations */}
                  <div>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:6 }}>Number of variations</div>
                    <div style={{ display:"flex", gap:6 }}>
                      {[1,2,3,4].map(n => (
                        <div key={n} onClick={()=>setVariations(n)} style={{ width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:T.rsm, fontSize:11, cursor:"default", background:variations===n?T.brand:"transparent", color:variations===n?"#fff":th.textSec, border:`1px solid ${variations===n?T.brand:th.border}`, fontWeight:variations===n?600:400 }}>{n}</div>
                      ))}
                    </div>
                  </div>
                  {/* Example output preview */}
                  <div style={{ padding:"10px 12px", borderRadius:T.rmd, background:th.muted, border:`1px solid ${th.border}`, borderStyle:"dashed" }}>
                    <div style={{ fontSize:9, color:th.textMuted, marginBottom:6 }}>Preview (last generated)</div>
                    <div style={{ display:"flex", gap:8 }}>
                      {[1,2].map(i => (
                        <div key={i} style={{ flex:1, padding:12, borderRadius:T.rsm, background:`linear-gradient(135deg, ${T.brand}15, ${T.cyan}10)`, border:`1px solid ${th.border}` }}>
                          <div style={{ height:8, width:"70%", borderRadius:4, background:th.text, opacity:0.15, marginBottom:6 }} />
                          <div style={{ height:6, width:"50%", borderRadius:4, background:th.text, opacity:0.08, marginBottom:4 }} />
                          <div style={{ height:20, width:"60%", borderRadius:4, background:T.brand, opacity:0.2 }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Footer */}
                <div style={{ padding:"12px 16px", borderTop:`1px solid ${th.border}`, display:"flex", justifyContent:"flex-end", gap:8 }}>
                  <MockBtn label="Cancel" type="outline" small />
                  <MockBtn label="Generate" type="primary" small />
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── AI CONNECTORS — AI Suggestions ─────────────────────────────────────────

    "ai-suggestions": () => {
      const suggestions = [
        { icon:"✨", label:"Add shadow", desc:"Drop shadow + blur" },
        { icon:"🎨", label:"Match brand colors", desc:"Apply theme palette" },
        { icon:"📐", label:"Resize to 200×200", desc:"Scale proportionally" },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              {/* Toolbar */}
              <div style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderBottom:`1px solid ${th.border}`, background:th.surface, flexWrap:"nowrap", overflow:"auto" }}>
                {["Select","Draw","Text","Rectangle","Ellipse","Connector","Eraser"].map(t => (
                  <div key={t} style={{ padding:"4px 8px", borderRadius:5, fontSize:mob?9:10, cursor:"default", background:t==="Select"?T.brand:th.muted, color:t==="Select"?"#fff":th.textSec, whiteSpace:"nowrap", fontWeight:t==="Select"?500:400 }}>{t}</div>
                ))}
                <div style={{ marginLeft:"auto", padding:"4px 8px", borderRadius:5, fontSize:10, background:T.violet+"20", color:T.violet, cursor:"default" }}>🤖 AI</div>
              </div>
              {/* Canvas */}
              <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", overflow:"hidden" }}>
                {/* Selected shape */}
                <div style={{ position:"absolute", left:mob?"25%":"35%", top:mob?"25%":"30%", width:mob?120:180, height:mob?80:120, background:T.brand, borderRadius:8, border:`2px solid ${T.brand}`, boxShadow:`0 0 0 2px ${T.brand}50`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:mob?12:16, color:"#fff", fontWeight:600, cursor:"default" }}>
                  Hero Block
                  {/* Selection handles */}
                  <div style={{ position:"absolute", left:-5, top:-5, width:10, height:10, borderRadius:"50%", background:"#fff", border:`2px solid ${T.brand}`, cursor:"default" }} />
                  <div style={{ position:"absolute", right:-5, top:-5, width:10, height:10, borderRadius:"50%", background:"#fff", border:`2px solid ${T.brand}`, cursor:"default" }} />
                  <div style={{ position:"absolute", left:-5, bottom:-5, width:10, height:10, borderRadius:"50%", background:"#fff", border:`2px solid ${T.brand}`, cursor:"default" }} />
                  <div style={{ position:"absolute", right:-5, bottom:-5, width:10, height:10, borderRadius:"50%", background:"#fff", border:`2px solid ${T.brand}`, cursor:"default" }} />
                </div>
                {/* Other shapes (unselected) */}
                <div style={{ position:"absolute", left:"55%", top:"25%", width:80, height:80, borderRadius:"50%", background:T.cyan, opacity:0.5 }} />
                <div style={{ position:"absolute", left:"30%", top:"65%", width:100, height:40, background:T.amber, borderRadius:4, opacity:0.4 }} />
                {/* Suggestion popover */}
                <div style={{ position:"absolute", left:mob?"5%":"52%", top:mob?"15%":"18%", width:mob?"90%":240, background:th.elevated, borderRadius:T.rmd, border:`1px solid ${th.borderMd}`, boxShadow:"0 4px 20px rgba(0,0,0,0.2)", cursor:"default", zIndex:10 }}>
                  {/* X dismiss */}
                  <div style={{ position:"absolute", right:6, top:6, width:16, height:16, borderRadius:"50%", background:th.muted, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, color:th.textMuted, cursor:"default" }}>✕</div>
                  {/* Header */}
                  <div style={{ padding:"10px 12px", borderBottom:`1px solid ${th.border}`, fontSize:11, fontWeight:600, color:th.text }}>🤖 AI Suggestions</div>
                  {/* Suggestion items */}
                  <div style={{ padding:"6px 12px", display:"flex", flexDirection:"column", gap:4 }}>
                    {suggestions.map(s => (
                      <div key={s.label} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:T.rsm, background:th.muted, cursor:"default" }}>
                        <span style={{ fontSize:12 }}>{s.icon}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:10, fontWeight:500, color:th.text }}>{s.label}</div>
                          <div style={{ fontSize:8, color:th.textMuted }}>{s.desc}</div>
                        </div>
                        <div style={{ padding:"2px 8px", borderRadius:4, fontSize:8, background:T.brandSoft, color:T.brand, cursor:"default", whiteSpace:"nowrap" }}>Apply</div>
                      </div>
                    ))}
                  </div>
                  {/* Footer */}
                  <div style={{ padding:"6px 12px", borderTop:`1px solid ${th.border}`, fontSize:8, color:th.textMuted, display:"flex", justifyContent:"space-between", cursor:"default" }}>
                    <span>3 suggestions</span>
                    <span style={{ color:T.brand, cursor:"default" }}>Dismiss all</span>
                  </div>
                </div>
                {/* Mobile — bottom sheet */}
                {mob && (
                  <div style={{ position:"absolute", bottom:0, left:0, right:0, background:th.surface, borderTop:`1px solid ${th.border}`, padding:8, zIndex:10 }}>
                    <div style={{ display:"flex", gap:6 }}>
                      {suggestions.slice(0,2).map(s => (
                        <div key={s.label} style={{ flex:1, padding:"6px 8px", borderRadius:T.rsm, background:th.muted, fontSize:9, color:th.text, display:"flex", alignItems:"center", gap:4, cursor:"default" }}>
                          <span>{s.icon}</span>
                          <span style={{ flex:1 }}>{s.label}</span>
                          <span style={{ fontSize:8, color:T.brand, cursor:"default" }}>Apply</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── AI CONNECTORS — Generation History ─────────────────────────────────────

    "ai-history": () => {
      const history = [
        { time:"2 min ago", prompt:"Create a hero section", status:"Applied", color:T.green },
        { time:"15 min ago", prompt:"Generate a button component with hover states", status:"Rejected", color:T.red },
        { time:"1h ago", prompt:"Add a gradient background to the header", status:"Applied", color:T.green },
        { time:"3h ago", prompt:"Create a color palette for SaaS brand", status:"Pending", color:T.amber },
        { time:"Yesterday", prompt:"Generate icon set for navigation", status:"Applied", color:T.green },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", background:th.bg }}>
              {/* Canvas area */}
              <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                <div style={{ fontSize:11, color:th.textMuted, opacity:0.4, cursor:"default" }}>Canvas</div>
              </div>
              {/* History panel — right sidebar */}
              <div style={{ width:mob?0:300, borderLeft:`1px solid ${th.border}`, background:th.surface, flexShrink:0, display:"flex", flexDirection:"column" }}>
                {/* Header */}
                <div style={{ padding:"10px 14px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ fontSize:12, fontWeight:600, color:th.text }}>🤖 Generation History</div>
                  <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
                </div>
                {/* History list */}
                <div style={{ flex:1, overflow:"auto", padding:"8px 10px", display:"flex", flexDirection:"column", gap:4 }}>
                  {history.map((h,i) => (
                    <div key={i} style={{ display:"flex", gap:8, padding:"8px 10px", borderRadius:T.rsm, background:th.muted, border:`1px solid ${th.border}`, cursor:"default", alignItems:"center" }}>
                      {/* Thumbnail */}
                      <div style={{ width:36, height:28, borderRadius:4, background:`linear-gradient(135deg, ${T.brand}30, ${T.cyan}20)`, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, opacity:0.6 }}>🎨</div>
                      {/* Details */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:9, fontWeight:600, color:th.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{h.prompt}</div>
                        <div style={{ fontSize:8, color:th.textMuted }}>{h.time}</div>
                      </div>
                      {/* Status badge */}
                      <div style={{ padding:"2px 6px", borderRadius:4, fontSize:8, fontWeight:500, background:h.color+"20", color:h.color, cursor:"default", whiteSpace:"nowrap" }}>{h.status}</div>
                    </div>
                  ))}
                </div>
                {/* Clear button */}
                <div style={{ padding:"10px 14px", borderTop:`1px solid ${th.border}` }}>
                  <MockBtn label="Clear history" type="outline" w="100%" small />
                </div>
              </div>
              {/* Mobile — full screen list */}
              {mob && (
                <div style={{ position:"absolute", inset:0, background:th.surface, zIndex:10, display:"flex", flexDirection:"column" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:th.text }}>🤖 Generation History</div>
                    <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
                  </div>
                  <div style={{ flex:1, overflow:"auto", padding:8, display:"flex", flexDirection:"column", gap:4 }}>
                    {history.map((h,i) => (
                      <div key={i} style={{ display:"flex", gap:8, padding:"8px 10px", borderRadius:T.rsm, background:th.muted, alignItems:"center", cursor:"default" }}>
                        <div style={{ width:36, height:28, borderRadius:4, background:`linear-gradient(135deg, ${T.brand}30, ${T.cyan}20)`, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, opacity:0.6 }}>🎨</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:10, fontWeight:500, color:th.text }}>{h.prompt}</div>
                          <div style={{ fontSize:8, color:th.textMuted }}>{h.time} · <span style={{ color:h.color }}>{h.status}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding:"10px 14px", borderTop:`1px solid ${th.border}` }}>
                    <MockBtn label="Clear history" type="outline" w="100%" small />
                  </div>
                </div>
              )}
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── DESIGN SYSTEM — DS Panel ───────────────────────────────────────────────

    "ds-panel": () => {
      const colorSwatches = [
        { name:"Primary", color:T.brand },
        { name:"Secondary", color:T.cyan },
        { name:"Neutral", color:"#64748b" },
        { name:"Success", color:T.green },
        { name:"Warning", color:T.amber },
        { name:"Danger", color:T.red },
      ];
      const fonts = [
        { name:"Inter", sizes:"12 / 14 / 16 / 20 / 24 / 32", sample:"Aa" },
        { name:"Roboto Mono", sizes:"10 / 12 / 14 / 16", sample:"Mono" },
      ];
      const spacing = [4,8,12,16,24,32];
      const radii = [
        { token:"rsm", value:"6px", label:"Small" },
        { token:"rmd", value:"10px", label:"Medium" },
        { token:"rlg", value:"16px", label:"Large" },
        { token:"rxl", value:"24px", label:"XLarge" },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", background:th.bg }}>
              {/* Canvas area */}
              <div style={{ flex:1, position:"relative", background:dark?"#0d1117":"#e8edf2", backgroundImage:`linear-gradient(${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.04)"} 1px,transparent 1px)`, backgroundSize:"20px 20px", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                <div style={{ fontSize:11, color:th.textMuted, opacity:0.4, cursor:"default" }}>Canvas</div>
              </div>
              {/* DS Panel — right sidebar */}
              <div style={{ width:mob?0:300, borderLeft:`1px solid ${th.border}`, background:th.surface, flexShrink:0, display:"flex", flexDirection:"column" }}>
                {/* Header */}
                <div style={{ padding:"10px 14px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:13 }}>🎨</span>
                    <span style={{ fontSize:12, fontWeight:600, color:th.text }}>Design System</span>
                  </div>
                  <div style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>✕</div>
                </div>
                {/* Scrollable sections */}
                <div style={{ flex:1, overflow:"auto" }}>
                  {/* Colors */}
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${th.border}` }}>
                    <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:8 }}>Colors</div>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {colorSwatches.map(c => (
                        <div key={c.name} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, cursor:"default" }}>
                          <div style={{ width:28, height:28, borderRadius:6, background:c.color, border:c.color==="#64748b"?`1px solid ${th.borderMd}`:"none" }} />
                          <span style={{ fontSize:7, color:th.textMuted }}>{c.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Typography */}
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${th.border}` }}>
                    <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:8 }}>Typography</div>
                    {fonts.map(f => (
                      <div key={f.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6, cursor:"default" }}>
                        <div>
                          <div style={{ fontSize:10, color:th.text }}>{f.name}</div>
                          <div style={{ fontSize:8, color:th.textMuted }}>{f.sizes}</div>
                        </div>
                        <div style={{ fontSize:18, fontWeight:600, color:th.text }}>{f.sample}</div>
                      </div>
                    ))}
                  </div>
                  {/* Spacing */}
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${th.border}` }}>
                    <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:8 }}>Spacing</div>
                    <div style={{ display:"flex", gap:6, alignItems:"flex-end" }}>
                      {spacing.map(s => (
                        <div key={s} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, cursor:"default" }}>
                          <div style={{ width:s*1.5, height:s, borderRadius:2, background:T.brand, opacity:0.5 }} />
                          <span style={{ fontSize:7, color:th.textMuted }}>{s}px</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Border Radius */}
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${th.border}` }}>
                    <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:8 }}>Border Radius</div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {radii.map(r => (
                        <div key={r.token} style={{ flex:1, minWidth:60, padding:"6px 8px", borderRadius:parseInt(r.value), background:th.muted, border:`1px solid ${th.border}`, textAlign:"center", cursor:"default" }}>
                          <div style={{ fontSize:9, color:th.text, fontWeight:500 }}>{r.value}</div>
                          <div style={{ fontSize:7, color:th.textMuted }}>{r.token}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Edit tokens link */}
                <div style={{ padding:"10px 14px", borderTop:`1px solid ${th.border}`, textAlign:"center" }}>
                  <span style={{ fontSize:10, color:T.brand, cursor:"default", textDecoration:"underline" }}>Edit tokens →</span>
                </div>
              </div>
              {/* Mobile — compact row */}
              {mob && (
                <div style={{ position:"absolute", bottom:0, left:0, right:0, background:th.surface, borderTop:`1px solid ${th.border}`, padding:"6px 10px", zIndex:10 }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                      <span style={{ fontSize:9, color:th.textSec }}>Colors</span>
                      <div style={{ display:"flex", gap:2 }}>
                        {colorSwatches.slice(0,4).map(c => (
                          <div key={c.name} style={{ width:12, height:12, borderRadius:3, background:c.color }} />
                        ))}
                      </div>
                    </div>
                    <span style={{ fontSize:9, color:T.brand, cursor:"default", marginLeft:"auto" }}>Edit</span>
                  </div>
                </div>
              )}
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── DESIGN SYSTEM — Component Library ──────────────────────────────────────

    "ds-components": () => {
      const components = [
        {
          name:"Button", preview:(
            <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
              <div style={{ padding:"4px 10px", borderRadius:6, fontSize:8, background:T.brand, color:"#fff", cursor:"default" }}>Primary</div>
              <div style={{ padding:"4px 10px", borderRadius:6, fontSize:8, border:`1px solid ${th.border}`, color:th.textSec, cursor:"default" }}>Outline</div>
              <div style={{ padding:"4px 10px", borderRadius:6, fontSize:8, border:`1px solid ${T.red}50`, color:T.red, cursor:"default" }}>Danger</div>
              <div style={{ padding:"4px 10px", borderRadius:6, fontSize:8, background:T.brandSoft, color:T.brand, cursor:"default" }}>Ghost</div>
            </div>
          ),
        },
        {
          name:"Input", preview:(
            <div style={{ height:24, borderRadius:5, border:`1px solid ${th.border}`, background:th.muted, display:"flex", alignItems:"center", padding:"0 8px", fontSize:8, color:th.textMuted, cursor:"default" }}>Placeholder text...</div>
          ),
        },
        {
          name:"Card", preview:(
            <div style={{ padding:8, borderRadius:T.rmd, border:`1px solid ${th.border}`, background:th.surface }}>
              <div style={{ width:"100%", height:12, borderRadius:3, background:th.muted, marginBottom:4 }} />
              <div style={{ width:"60%", height:8, borderRadius:3, background:th.muted }} />
            </div>
          ),
        },
        {
          name:"Badge", preview:(
            <div style={{ display:"flex", gap:4 }}>
              <span style={{ padding:"2px 6px", borderRadius:4, fontSize:8, background:T.greenSoft, color:T.green, cursor:"default" }}>Active</span>
              <span style={{ padding:"2px 6px", borderRadius:4, fontSize:8, background:T.amberSoft, color:T.amber, cursor:"default" }}>Pending</span>
              <span style={{ padding:"2px 6px", borderRadius:4, fontSize:8, background:T.redSoft, color:T.red, cursor:"default" }}>Error</span>
            </div>
          ),
        },
        {
          name:"Modal", preview:(
            <div style={{ padding:8, borderRadius:T.rmd, border:`1px solid ${th.border}`, background:th.surface }}>
              <div style={{ fontSize:8, fontWeight:600, color:th.text, marginBottom:4, display:"flex", justifyContent:"space-between" }}>
                <span>Title</span><span style={{ color:th.textMuted }}>✕</span>
              </div>
              <div style={{ width:"100%", height:6, borderRadius:3, background:th.muted, marginBottom:4 }} />
              <div style={{ width:"70%", height:6, borderRadius:3, background:th.muted, marginBottom:4 }} />
              <div style={{ display:"flex", gap:4, justifyContent:"flex-end" }}>
                <div style={{ padding:"2px 6px", borderRadius:4, fontSize:7, border:`1px solid ${th.border}`, color:th.textMuted, cursor:"default" }}>Cancel</div>
                <div style={{ padding:"2px 6px", borderRadius:4, fontSize:7, background:T.brand, color:"#fff", cursor:"default" }}>Confirm</div>
              </div>
            </div>
          ),
        },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              {/* Header */}
              <div style={{ padding:mob?"10px 12px":"14px 20px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
                <Row align="flex-start" wrap>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:mob?14:18, fontWeight:600, color:th.text }}>🎨 Component Library</div>
                    <div style={{ fontSize:10, color:th.textSec, marginTop:2 }}>Drag components onto the canvas</div>
                  </div>
                  {!mob && <MockField placeholder="🔍 Search components..." w={200} />}
                </Row>
                {mob && <div style={{ marginTop:8 }}><MockField placeholder="🔍 Search components..." w="100%" /></div>}
              </div>
              {/* Component grid */}
              <div style={{ flex:1, padding:mob?10:16, overflow:"auto" }}>
                <div style={{ display:"grid", gridTemplateColumns:`repeat(${mob?1:3}, 1fr)`, gap:mob?10:16 }}>
                  {components.map(c => (
                    <div key={c.name} style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, overflow:"hidden", cursor:"default" }}>
                      <div style={{ padding:"12px 14px", borderBottom:`1px solid ${th.border}` }}>
                        <div style={{ fontSize:11, fontWeight:600, color:th.text, marginBottom:4 }}>{c.name}</div>
                        {c.preview}
                      </div>
                      <div style={{ padding:"8px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span style={{ fontSize:8, color:th.textMuted }}>v1.0</span>
                        <MockBtn label="Use" type="primary" small />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── DESIGN SYSTEM — Token Editor ───────────────────────────────────────────

    "ds-tokens": () => {
      const [search, setSearch] = useState("");
      const tokens = [
        { name:"--zh-primary", value:"#5e4ce2", type:"Color", source:"Global" },
        { name:"--zh-secondary", value:"#06b6d4", type:"Color", source:"Global" },
        { name:"--zh-surface", value:"#ffffff", type:"Color", source:"Global" },
        { name:"--zh-text", value:"#0f172a", type:"Color", source:"Global" },
        { name:"--zh-text-sec", value:"#475569", type:"Color", source:"Global" },
        { name:"--zh-border-radius", value:"6px", type:"Spacing", source:"Global" },
        { name:"--zh-font-family", value:"Inter, sans-serif", type:"Typography", source:"Global" },
        { name:"--zh-font-mono", value:"Roboto Mono", type:"Typography", source:"Global" },
        { name:"--zh-btn-padding", value:"8px 16px", type:"Spacing", source:"Component" },
        { name:"--zh-card-shadow", value:"0 1px 3px rgba(0,0,0,0.1)", type:"Color", source:"Component" },
      ];
      const filtered = tokens.filter(t => t.name.includes(search) || t.type.includes(search));
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:th.bg }}>
              {/* Header */}
              <div style={{ padding:mob?"10px 12px":"14px 20px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
                <Row align="flex-start" wrap>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:mob?14:18, fontWeight:600, color:th.text }}>🔤 Token Editor</div>
                    <div style={{ fontSize:10, color:th.textSec, marginTop:2 }}>{tokens.length} tokens</div>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                    <MockField placeholder="🔍 Search tokens..." w={mob?"100%":180} />
                    <MockBtn label="+ Add token" type="primary" small />
                  </div>
                </Row>
                {mob && <div style={{ marginTop:6 }}><MockField placeholder="🔍 Search tokens..." w="100%" /></div>}
              </div>
              {/* Table */}
              <div style={{ flex:1, padding:mob?10:16, overflow:"auto" }}>
                {/* Table header */}
                {!mob && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 120px 100px 100px 80px", gap:8, padding:"8px 12px", fontSize:9, fontWeight:600, color:th.textMuted, borderBottom:`1px solid ${th.border}`, marginBottom:2 }}>
                    <span>Token Name</span><span>Value</span><span>Type</span><span>Source</span><span />
                  </div>
                )}
                {/* Rows */}
                <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                  {filtered.map((t,i) => (
                    <div key={t.name} style={{ display:"grid", gridTemplateColumns:mob?"1fr":"1fr 120px 100px 100px 80px", gap:8, padding:mob?"8px 0":"8px 12px", background:i%2===0?th.muted:"transparent", borderRadius:T.rsm, alignItems:"center", cursor:"default" }}>
                      <div>
                        {mob && <span style={{ fontSize:9, color:th.textMuted, display:"block", marginBottom:2 }}>{t.type} · {t.source}</span>}
                        <span style={{ fontSize:10, color:th.text, fontFamily:"monospace" }}>{t.name}</span>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        {t.type==="Color" && <div style={{ width:14, height:14, borderRadius:3, background:t.value, border:`1px solid ${th.borderMd}`, flexShrink:0 }} />}
                        <span style={{ fontSize:9, color:th.textSec, fontFamily:"monospace" }}>{t.value}</span>
                      </div>
                      <div>{!mob && <span style={{ fontSize:9, padding:"2px 6px", borderRadius:4, background:t.type==="Color"?T.brandSoft:t.type==="Typography"?T.cyanSoft:T.amberSoft, color:t.type==="Color"?T.brand:t.type==="Typography"?T.cyan:T.amber, cursor:"default" }}>{t.type}</span>}</div>
                      <div>{!mob && <MockBadge type="neutral" label={t.source} />}</div>
                      <div style={{ textAlign:"right" }}>
                        <span style={{ fontSize:9, color:T.brand, cursor:"default", textDecoration:"underline" }}>Edit</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    // ─── DESIGN SYSTEM — Theme Switcher ─────────────────────────────────────────

    "ds-themes": () => {
      const [sel, setSel] = useState("light");
      const [accent, setAccent] = useState("#5e4ce2");
      const themes = [
        { id:"light", label:"Light", icon:"☀️", bg:"#ffffff", text:"#0f172a", surface:"#f8fafc", border:"rgba(51,65,85,0.1)" },
        { id:"dark", label:"Dark", icon:"🌙", bg:"#0a0d12", text:"#f1f5f9", surface:"#111318", border:"rgba(255,255,255,0.08)" },
      ];
      const presets = ["#5e4ce2","#06b6d4","#14b8a6","#f59e0b","#f97316","#ef4444","#7c3aed"];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", padding:mob?12:20, overflow:"auto" }}>
              <div style={{ fontSize:mob?14:18, fontWeight:600, color:th.text, marginBottom:16 }}>Themes</div>
              <div style={{ display:"grid", gridTemplateColumns:`repeat(${mob?1:2}, 1fr)`, gap:12, marginBottom:20 }}>
                {themes.map(t => (
                  <div key={t.id} onClick={()=>setSel(t.id)} style={{ display:"flex", alignItems:"center", gap:10, padding:14, borderRadius:T.rmd, cursor:"default", background:t.bg, border:sel===t.id?`2px solid ${T.brand}`:`1px solid ${th.border}`, boxShadow:sel===t.id?`0 0 0 2px ${T.brandSoft}`:"none" }}>
                    <div style={{ fontSize:24 }}>{t.icon}</div>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:t.text }}>{t.label}</div>
                      <div style={{ fontSize:9, color:th.textMuted, marginTop:2 }}>{t.bg} · {t.text}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:8 }}>Custom Accent</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {presets.map(c => (
                    <div key={c} onClick={()=>setAccent(c)} style={{ width:32, height:32, borderRadius:"50%", background:c, cursor:"default", border:accent===c?`3px solid ${th.borderMd}`:"3px solid transparent", boxShadow:accent===c?`0 0 0 2px ${T.brand}`:"none" }} />
                  ))}
                  <div style={{ width:32, height:32, borderRadius:"50%", background:`linear-gradient(135deg, ${T.brand}, ${T.cyan}, ${T.amber})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, cursor:"default", border:`1px solid ${th.border}` }}>+</div>
                </div>
              </div>
              <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16 }}>
                <div style={{ fontSize:11, fontWeight:600, color:th.textSec, marginBottom:10 }}>Preview</div>
                <div style={{ display:"flex", gap:8, flexDirection:mob?"column":"row", alignItems:mob?"stretch":"center" }}>
                  <div style={{ padding:"8px 16px", background:accent, color:"#fff", borderRadius:T.rsm, fontSize:11, fontWeight:500, cursor:"default", textAlign:"center" }}>Primary Button</div>
                  <div style={{ padding:"8px 16px", background:th.muted, color:th.text, borderRadius:T.rsm, fontSize:11, border:`1px solid ${th.border}`, cursor:"default", textAlign:"center" }}>Outline Button</div>
                  <div style={{ padding:"8px 12px", borderRadius:T.rsm, fontSize:10, color:th.text, border:`1px solid ${th.border}`, display:"flex", alignItems:"center", gap:6, cursor:"default" }}>
                    <MockField placeholder="Input preview..." w={mob?"100%":140} />
                  </div>
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "settings-general": () => {
      const tabs = [
        { key:"general", label:"General", icon:"⚙️" },
        { key:"members", label:"Members", icon:"👥" },
        { key:"billing", label:"Billing", icon:"💳" },
        { key:"profile", label:"Profile", icon:"👤" },
      ];
      const [activeTab, setActiveTab] = useState("general");
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:mob?"column":"row" }}>
              <div style={{ width:mob?"100%":200, borderRight:mob?"none":`1px solid ${th.border}`, borderBottom:mob?`1px solid ${th.border}`:"none", background:th.surface, display:"flex", flexDirection:mob?"row":"column", padding:mob?"0":"8px 0", overflow:"auto" }}>
                {tabs.map(t => (
                  <div key={t.key} onClick={()=>setActiveTab(t.key)} style={{ display:"flex", alignItems:"center", gap:8, padding:mob?"8px 12px":"8px 16px", cursor:"default", background:activeTab===t.key?T.brandSoft:"transparent", color:activeTab===t.key?T.brand:th.textSec, borderRight:!mob && activeTab===t.key?`2px solid ${T.brand}`:"2px solid transparent", borderBottom:mob && activeTab===t.key?`2px solid ${T.brand}`:"2px solid transparent", fontSize:mob?10:12, fontWeight:activeTab===t.key?500:400, whiteSpace:"nowrap" }}>
                    <span style={{ fontSize:mob?12:14 }}>{t.icon}</span>
                    <span>{t.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ flex:1, padding:mob?12:20, overflow:"auto" }}>
                <div style={{ maxWidth:600 }}>
                  <div style={{ fontSize:mob?16:20, fontWeight:600, color:th.text, marginBottom:20 }}>General</div>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Workspace name</div>
                    <MockField value="Acme Corp" w="100%" />
                  </div>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:6 }}>Workspace logo</div>
                    <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                      <div style={{ width:56, height:56, borderRadius:8, background:`linear-gradient(135deg, ${T.brandSoft}, ${T.cyanSoft})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:T.brand, cursor:"default", border:`1px solid ${th.borderMd}` }}>🏢</div>
                      <div style={{ padding:"6px 12px", borderRadius:6, fontSize:10, border:`1px solid ${th.border}`, background:th.muted, color:th.textSec, cursor:"default" }}>Upload logo</div>
                      <div style={{ fontSize:9, color:th.textMuted }}>PNG, SVG. Max 2MB</div>
                    </div>
                  </div>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Default role for new members</div>
                    <div style={{ padding:"6px 10px", borderRadius:6, border:`1px solid ${th.border}`, background:th.muted, fontSize:11, color:th.text, display:"flex", alignItems:"center", cursor:"default", width:mob?"100%":200 }}>Editor ▾</div>
                  </div>
                  <div style={{ marginBottom:24 }}>
                    <MockBtn label="Save changes" type="primary" small />
                  </div>
                  <div style={{ border:`1px solid ${T.red}50`, borderRadius:T.rmd, padding:16, background:T.redSoft }}>
                    <div style={{ fontSize:12, fontWeight:600, color:T.red, marginBottom:4 }}>Danger Zone</div>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:12 }}>Permanently delete your workspace and all associated data. This action cannot be undone.</div>
                    <MockBtn label="Delete workspace" type="danger" small />
                  </div>
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "settings-members": () => {
      const members = [
        { name:"Alice Chen", email:"alice@acmecorp.com", role:"Editor", joined:"Jan 2026", color:T.red },
        { name:"Bob Martinez", email:"bob@acmecorp.com", role:"Viewer", joined:"Jan 2026", color:T.amber },
        { name:"Charlie Park", email:"charlie@acmecorp.com", role:"Commenter", joined:"Feb 2026", color:T.green },
        { name:"Diana Lee", email:"diana@acmecorp.com", role:"Editor", joined:"Feb 2026", color:T.violet },
        { name:"Eve Johnson", email:"eve@acmecorp.com", role:"Viewer", joined:"Mar 2026", color:T.coral },
        { name:"Frank Zhang", email:"frank@acmecorp.com", role:"Editor", joined:"Mar 2026", color:T.cyan },
      ];
      const roleColors = { Editor:T.brand, Viewer:T.amber, Commenter:T.teal };
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:mob?"column":"row" }}>
              <div style={{ width:mob?"100%":200, borderRight:mob?"none":`1px solid ${th.border}`, borderBottom:mob?`1px solid ${th.border}`:"none", background:th.surface, display:"flex", flexDirection:mob?"row":"column", padding:mob?"0":"8px 0", overflow:"auto" }}>
                {["General","Members","Billing","Profile"].map((tab,i) => (
                  <div key={tab} style={{ display:"flex", alignItems:"center", gap:8, padding:mob?"8px 12px":"8px 16px", cursor:"default", background:i===1?T.brandSoft:"transparent", color:i===1?T.brand:th.textSec, borderRight:i===1 && !mob?`2px solid ${T.brand}`:"2px solid transparent", borderBottom:i===1 && mob?`2px solid ${T.brand}`:"2px solid transparent", fontSize:mob?10:12, fontWeight:i===1?500:400, whiteSpace:"nowrap" }}>
                    <span style={{ fontSize:mob?12:14 }}>{["⚙️","👥","💳","👤"][i]}</span>
                    <span>{tab}</span>
                  </div>
                ))}
              </div>
              <div style={{ flex:1, padding:mob?12:20, display:"flex", flexDirection:"column", overflow:"auto" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexDirection:mob?"column":"row", gap:mob?8:0, marginBottom:16 }}>
                  <div>
                    <div style={{ fontSize:mob?16:20, fontWeight:600, color:th.text }}>Members</div>
                    <div style={{ fontSize:11, color:th.textSec, marginTop:2 }}>{members.length} members</div>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center", width:mob?"100%":"auto" }}>
                    {!mob && <MockField placeholder="🔍 Search members..." w={200} />}
                    <MockBtn label="+ Invite member" type="primary" small />
                  </div>
                </div>
                {mob && <div style={{ marginBottom:12 }}><MockField placeholder="🔍 Search members..." w="100%" /></div>}
                {!mob && (
                  <div style={{ display:"grid", gridTemplateColumns:"36px 1fr 1fr 100px 110px 140px", gap:8, padding:"8px 12px", fontSize:9, fontWeight:600, color:th.textMuted, borderBottom:`1px solid ${th.border}`, marginBottom:2 }}>
                    <span /><span>Name</span><span>Email</span><span>Role</span><span>Joined</span><span>Actions</span>
                  </div>
                )}
                <div style={{ display:"flex", flexDirection:"column", gap:2, flex:1 }}>
                  {members.map((m,i) => (
                    <div key={m.email} style={{ display:"grid", gridTemplateColumns:mob?"1fr":"36px 1fr 1fr 100px 110px 140px", gap:8, padding:mob?"8px 0":"8px 12px", background:i%2===0?th.muted:"transparent", borderRadius:T.rsm, alignItems:"center", cursor:"default" }}>
                      {!mob && <div style={{ width:24, height:24, borderRadius:"50%", background:m.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, color:"#fff", fontWeight:600 }}>{m.name.split(" ").map(n=>n[0]).join("")}</div>}
                      <div><span style={{ fontSize:11, color:th.text }}>{m.name}</span></div>
                      <div><span style={{ fontSize:9, color:th.textMuted }}>{m.email}</span></div>
                      <div><MockBadge type="neutral" label={m.role} style={{ background:roleColors[m.role]+"20", color:roleColors[m.role] }} /></div>
                      <div><span style={{ fontSize:9, color:th.textMuted }}>{m.joined}</span></div>
                      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                        <div style={{ padding:"2px 6px", borderRadius:4, fontSize:8, border:`1px solid ${th.border}`, color:th.textSec, cursor:"default", whiteSpace:"nowrap" }}>Role ▾</div>
                        <div style={{ fontSize:11, color:th.textMuted, cursor:"default" }}>✕</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "settings-billing": () => {
      const invoices = [
        { date:"01 Jun 2026", amount:"$45.00", status:"Paid", color:T.green },
        { date:"01 May 2026", amount:"$45.00", status:"Paid", color:T.green },
        { date:"01 Apr 2026", amount:"$45.00", status:"Paid", color:T.green },
        { date:"01 Mar 2026", amount:"$45.00", status:"Paid", color:T.green },
        { date:"01 Feb 2026", amount:"$45.00", status:"Pending", color:T.amber },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:mob?"column":"row" }}>
              <div style={{ width:mob?"100%":200, borderRight:mob?"none":`1px solid ${th.border}`, borderBottom:mob?`1px solid ${th.border}`:"none", background:th.surface, display:"flex", flexDirection:mob?"row":"column", padding:mob?"0":"8px 0", overflow:"auto" }}>
                {["General","Members","Billing","Profile"].map((tab,i) => (
                  <div key={tab} style={{ display:"flex", alignItems:"center", gap:8, padding:mob?"8px 12px":"8px 16px", cursor:"default", background:i===2?T.brandSoft:"transparent", color:i===2?T.brand:th.textSec, borderRight:i===2 && !mob?`2px solid ${T.brand}`:"2px solid transparent", borderBottom:i===2 && mob?`2px solid ${T.brand}`:"2px solid transparent", fontSize:mob?10:12, fontWeight:i===2?500:400, whiteSpace:"nowrap" }}>
                    <span style={{ fontSize:mob?12:14 }}>{["⚙️","👥","💳","👤"][i]}</span>
                    <span>{tab}</span>
                  </div>
                ))}
              </div>
              <div style={{ flex:1, padding:mob?12:20, overflow:"auto" }}>
                <div style={{ fontSize:mob?16:20, fontWeight:600, color:th.text, marginBottom:20 }}>Billing</div>
                <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16, marginBottom:14 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:th.text }}>Current Plan</div>
                      <div style={{ fontSize:10, color:th.textMuted, marginTop:2 }}>Pro · $15/user/mo</div>
                    </div>
                    <MockBadge label="Active" type="active" />
                  </div>
                  {[
                    { label:"Members", used:"8", total:"25", pct:32, color:T.brand },
                    { label:"AI Tokens", used:"4.2K", total:"10K", pct:42, color:T.cyan },
                    { label:"Storage", used:"600MB", total:"1GB", pct:60, color:T.amber },
                  ].map(u => (
                    <div key={u.label} style={{ marginBottom:6 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:th.textSec, marginBottom:3 }}>
                        <span>{u.label}</span><span>{u.used} of {u.total}</span>
                      </div>
                      <div style={{ height:6, borderRadius:3, background:th.muted, overflow:"hidden" }}>
                        <div style={{ width:`${u.pct}%`, height:"100%", borderRadius:3, background:u.color }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16, marginBottom:14 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, justifyContent:"space-between" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ fontSize:18 }}>💳</div>
                      <div>
                        <div style={{ fontSize:11, color:th.text }}>Visa ending in 4242</div>
                        <div style={{ fontSize:9, color:th.textMuted }}>Expires 12/27</div>
                      </div>
                    </div>
                    <div style={{ fontSize:10, color:T.brand, cursor:"default", textDecoration:"underline" }}>Update</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, flexDirection:mob?"column":"row", marginBottom:20 }}>
                  <MockBtn label="Upgrade to Enterprise" type="primary" small />
                  <MockBtn label="Cancel subscription" type="danger" small />
                </div>
                <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:10 }}>Invoice History</div>
                {!mob && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8, padding:"8px 12px", fontSize:9, fontWeight:600, color:th.textMuted, borderBottom:`1px solid ${th.border}`, marginBottom:2 }}>
                    <span>Date</span><span>Amount</span><span>Status</span><span />
                  </div>
                )}
                <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                  {invoices.map((inv,i) => (
                    <div key={i} style={{ display:"grid", gridTemplateColumns:mob?"1fr":"1fr 1fr 1fr 1fr", gap:8, padding:mob?"8px 0":"8px 12px", background:i%2===0?th.muted:"transparent", borderRadius:T.rsm, alignItems:"center", cursor:"default" }}>
                      <span style={{ fontSize:10, color:th.text }}>{inv.date}</span>
                      <span style={{ fontSize:10, color:th.text }}>{inv.amount}</span>
                      <div><MockBadge type="neutral" label={inv.status} style={{ background:inv.color+"20", color:inv.color }} /></div>
                      <div style={{ fontSize:10, color:T.brand, cursor:"default", textDecoration:"underline" }}>{mob?"PDF":"Download PDF"}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "settings-profile": () => {
      const [themePref, setThemePref] = useState("system");
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:mob?"column":"row" }}>
              <div style={{ width:mob?"100%":200, borderRight:mob?"none":`1px solid ${th.border}`, borderBottom:mob?`1px solid ${th.border}`:"none", background:th.surface, display:"flex", flexDirection:mob?"row":"column", padding:mob?"0":"8px 0", overflow:"auto" }}>
                {["General","Members","Billing","Profile"].map((tab,i) => (
                  <div key={tab} style={{ display:"flex", alignItems:"center", gap:8, padding:mob?"8px 12px":"8px 16px", cursor:"default", background:i===3?T.brandSoft:"transparent", color:i===3?T.brand:th.textSec, borderRight:i===3 && !mob?`2px solid ${T.brand}`:"2px solid transparent", borderBottom:i===3 && mob?`2px solid ${T.brand}`:"2px solid transparent", fontSize:mob?10:12, fontWeight:i===3?500:400, whiteSpace:"nowrap" }}>
                    <span style={{ fontSize:mob?12:14 }}>{["⚙️","👥","💳","👤"][i]}</span>
                    <span>{tab}</span>
                  </div>
                ))}
              </div>
              <div style={{ flex:1, padding:mob?12:20, overflow:"auto" }}>
                <div style={{ maxWidth:550 }}>
                  <div style={{ fontSize:mob?16:20, fontWeight:600, color:th.text, marginBottom:20 }}>Profile</div>
                  <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:20 }}>
                    <div style={{ width:64, height:64, borderRadius:"50%", background:T.brandSoft, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, color:T.brand, cursor:"default", border:`2px solid ${th.borderMd}` }}>👤</div>
                    <div>
                      <MockBtn label="Upload photo" type="outline" small />
                      <div style={{ fontSize:9, color:th.textMuted, marginTop:4 }}>PNG, JPG. Max 2MB. Square.</div>
                    </div>
                  </div>
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Display name</div>
                    <MockField value="Alex Rivera" w="100%" />
                  </div>
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Email</div>
                    <div style={{ padding:"6px 10px", borderRadius:6, border:`1px solid ${th.border}`, background:th.muted, fontSize:11, display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"default" }}>
                      <span style={{ color:th.textMuted }}>alex@acmecorp.com</span>
                      <span style={{ fontSize:9, color:T.green }}>✓ Verified</span>
                    </div>
                  </div>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:8 }}>Theme preference</div>
                    <div style={{ display:"flex", gap:8, flexDirection:mob?"column":"row" }}>
                      {["Light","Dark","System"].map(t => (
                        <div key={t} onClick={()=>setThemePref(t.toLowerCase())} style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:T.rsm, cursor:"default", background:themePref===t.toLowerCase()?T.brandSoft:"transparent", border:`1px solid ${themePref===t.toLowerCase()?T.brandBorder:th.border}`, color:themePref===t.toLowerCase()?T.brand:th.textSec, fontSize:10 }}>
                          <div style={{ width:14, height:14, borderRadius:"50%", border:`1px solid ${themePref===t.toLowerCase()?T.brand:th.borderMd}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                            {themePref===t.toLowerCase() && <div style={{ width:8, height:8, borderRadius:"50%", background:T.brand }} />}
                          </div>
                          {t}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:10, color:th.textSec, marginBottom:6 }}>Bio</div>
                    <div style={{ padding:"8px 10px", borderRadius:6, border:`1px solid ${th.border}`, background:th.muted, fontSize:11, color:th.textMuted, cursor:"default", minHeight:60, lineHeight:1.5 }}>Design lead at Acme Corp. Passionate about design systems and AI-powered creativity.</div>
                  </div>
                  <MockBtn label="Save changes" type="primary" small />
                </div>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },

    "onboarding-welcome": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:th.bg, padding:mob?20:40 }}>
          <div style={{ textAlign:"center", maxWidth:680 }}>
            <div style={{ fontSize:56, marginBottom:8 }}>🎨</div>
            <div style={{ fontSize:mob?22:30, fontWeight:700, color:th.text, marginBottom:8 }}>Welcome to ZiniPixels</div>
            <div style={{ fontSize:mob?12:14, color:th.textSec, marginBottom:32, maxWidth:480, margin:"0 auto", lineHeight:1.5 }}>
              The AI-powered design platform for creating stunning interfaces, collaborating in real-time, and sharing securely with your team.
            </div>
            <div style={{ display:"grid", gridTemplateColumns:`repeat(${mob?1:3}, 1fr)`, gap:mob?10:16, marginBottom:32 }}>
              {[
                { icon:"🎨", title:"Design with AI", desc:"Generate layouts, components, and full pages from natural language prompts." },
                { icon:"👥", title:"Collaborate in real-time", desc:"Multiplayer editing with live cursors, comments, and review workflows." },
                { icon:"🔗", title:"Share securely", desc:"Role-based access, shareable links, and granular permission controls." },
              ].map(f => (
                <div key={f.title} style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:mob?12:20, cursor:"default", textAlign:"center" }}>
                  <div style={{ fontSize:mob?28:36, marginBottom:8 }}>{f.icon}</div>
                  <div style={{ fontSize:mob?12:14, fontWeight:600, color:th.text, marginBottom:4 }}>{f.title}</div>
                  <div style={{ fontSize:mob?10:11, color:th.textSec, lineHeight:1.5 }}>{f.desc}</div>
                </div>
              ))}
            </div>
            <MockBtn label="Get Started" type="primary" w={mob?"100%":200} />
            <div style={{ marginTop:16 }}>
              <span style={{ fontSize:12, color:th.textMuted }}>Already have an account? </span>
              <span style={{ fontSize:12, color:T.brand, cursor:"default", textDecoration:"underline" }}>Sign in</span>
            </div>
          </div>
        </div>
      </WF>
    ),

    "onboarding-plan": () => {
      const [annual, setAnnual] = useState(false);
      const plans = [
        { name:"Free", price:"$0", period:"forever", popular:false, features:["3 projects","3 members","1K AI tokens / mo","50MB storage","Basic support"], cta:"Get Started Free", type:"outline" },
        { name:"Pro", price:"$15", period:"/user/mo", popular:true, features:["Unlimited projects","25 members","10K AI tokens / mo","1GB storage","Priority support","AI design generation"], cta:"Start Pro", type:"primary" },
        { name:"Enterprise", price:"Custom", period:"", popular:false, features:["Everything in Pro","SSO & SAML","Custom storage","Dedicated support","SLA guarantee","Audit logs"], cta:"Contact Sales", type:"outline" },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:th.bg, padding:mob?16:32 }}>
            <div style={{ width:"100%", maxWidth:860, textAlign:"center" }}>
              <div style={{ fontSize:mob?20:28, fontWeight:700, color:th.text, marginBottom:4 }}>Choose your plan</div>
              <div style={{ fontSize:12, color:th.textSec, marginBottom:24 }}>Start free, upgrade when you need more power.</div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:28 }}>
                <span onClick={()=>setAnnual(false)} style={{ fontSize:12, cursor:"default", fontWeight:annual?400:600, color:annual?th.textMuted:th.text }}>Monthly</span>
                <div onClick={()=>setAnnual(!annual)} style={{ width:44, height:22, borderRadius:11, background:annual?T.brand:th.borderMd, position:"relative", cursor:"default" }}>
                  <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:annual?24:2, transition:"left 0.15s ease" }} />
                </div>
                <span onClick={()=>setAnnual(true)} style={{ fontSize:12, cursor:"default", fontWeight:annual?600:400, color:annual?th.text:th.textMuted }}>
                  Annual <span style={{ color:T.green, fontWeight:500 }}>Save 20%</span>
                </span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:`repeat(${mob?1:3}, 1fr)`, gap:mob?10:16, textAlign:"left" }}>
                {plans.map(p => (
                  <div key={p.name} style={{ background:th.surface, borderRadius:T.rmd, border:p.popular?`2px solid ${T.brand}`:`1px solid ${th.border}`, padding:mob?14:20, position:"relative", cursor:"default", boxShadow:p.popular?`0 4px 20px ${T.brand}20`:"none" }}>
                    {p.popular && <div style={{ position:"absolute", top:-1, left:"50%", transform:"translateX(-50%)", background:T.brand, color:"#fff", fontSize:9, fontWeight:600, padding:"3px 16px", borderRadius:"0 0 8px 8px", cursor:"default" }}>Popular</div>}
                    <div style={{ fontSize:16, fontWeight:700, color:th.text, marginBottom:4, marginTop:p.popular?16:0 }}>{p.name}</div>
                    <div style={{ fontSize:28, fontWeight:700, color:th.text, marginBottom:2 }}>
                      {p.price}
                      {p.period && <span style={{ fontSize:11, fontWeight:400, color:th.textMuted }}>{p.period}</span>}
                    </div>
                    {p.name==="Pro" && annual && <div style={{ fontSize:10, color:T.green, fontWeight:500, marginBottom:8 }}>$12/user/mo billed annually</div>}
                    <div style={{ display:"flex", flexDirection:"column", gap:4, marginTop:12, marginBottom:16 }}>
                      {p.features.map(f => (
                        <div key={f} style={{ display:"flex", alignItems:"center", gap:6, fontSize:10, color:th.textSec }}>
                          <span style={{ color:T.green }}>✓</span>
                          {f}
                        </div>
                      ))}
                    </div>
                    <MockBtn label={p.cta} type={p.type} w="100%" small />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </WF>
      );
    },

    "onboarding-payment": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:th.bg, padding:mob?16:32 }}>
          <div style={{ width:"100%", maxWidth:520 }}>
            <div style={{ fontSize:mob?18:24, fontWeight:700, color:th.text, marginBottom:24, textAlign:"center" }}>Set up your subscription</div>
            <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16, marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:600, color:th.text }}>Plan Summary</div>
                <MockBadge label="Pro" type="professional" />
              </div>
              <div style={{ fontSize:11, color:th.textSec }}>Pro · $15/user/mo · Annual · 3 users = <strong style={{ color:th.text }}>$36/mo</strong></div>
              <div style={{ fontSize:9, color:T.green, marginTop:4 }}>✓ Save 20% with annual billing</div>
            </div>
            <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16, marginBottom:16 }}>
              <div style={{ fontSize:10, fontWeight:600, color:th.textSec, marginBottom:8 }}>Payment details</div>
              <div style={{ padding:"10px 12px", borderRadius:T.rsm, border:`1px solid ${th.border}`, background:th.muted, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"default" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:14 }}>💳</span>
                  <span style={{ fontSize:11, color:th.text }}>4242 4242 4242 4242</span>
                </div>
                <span style={{ fontSize:9, color:th.textMuted }}>MM/YY CVC</span>
              </div>
              <div style={{ marginBottom:6 }}><MockField placeholder="Full name" w="100%" /></div>
              <div style={{ marginBottom:6 }}><MockField placeholder="Country" w="100%" /></div>
              <div style={{ marginBottom:6 }}><MockField placeholder="Postal code" w="100%" /></div>
            </div>
            <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16, marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:th.textSec, marginBottom:4 }}>
                <span>Subtotal</span><span>$36.00/mo</span>
              </div>
              <div style={{ height:1, background:th.border, margin:"4px 0" }} />
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, fontWeight:600, color:th.text }}>
                <span>Total</span><span>$36.00/mo</span>
              </div>
            </div>
            <MockBtn label="Start subscription" type="primary" w="100%" small />
            <div style={{ textAlign:"center", marginTop:10, fontSize:10, color:th.textMuted, cursor:"default" }}>
              🔒 Secure payments powered by Stripe
            </div>
            <div style={{ textAlign:"center", marginTop:12 }}>
              <span style={{ fontSize:11, color:T.brand, cursor:"default", textDecoration:"underline" }}>← Back</span>
            </div>
          </div>
        </div>
      </WF>
    ),

    "billing-portal": () => {
      const invoices = [
        { date:"01 Jun 2026", num:"INV-001", amount:"$45.00", status:"Paid", color:T.green },
        { date:"01 May 2026", num:"INV-002", amount:"$45.00", status:"Paid", color:T.green },
        { date:"01 Apr 2026", num:"INV-003", amount:"$45.00", status:"Paid", color:T.green },
        { date:"01 Mar 2026", num:"INV-004", amount:"$45.00", status:"Paid", color:T.green },
        { date:"01 Feb 2026", num:"INV-005", amount:"$45.00", status:"Pending", color:T.amber },
      ];
      return (
        <WF dark={dark} viewport={viewport} slim={slim} domain="app.zinipixels.com">
          <ZiniPixelsShell dark={dark} viewport={viewport} role={role}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", padding:mob?12:20, overflow:"auto" }}>
              <div style={{ fontSize:mob?16:20, fontWeight:600, color:th.text, marginBottom:20 }}>Billing</div>
              <div style={{ display:"grid", gridTemplateColumns:mob?"1fr":"1fr 1fr", gap:mob?10:14, marginBottom:16 }}>
                <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:th.text }}>Current Plan</div>
                      <div style={{ fontSize:10, color:th.textMuted, marginTop:2 }}>Pro</div>
                    </div>
                    <MockBadge label="Active" type="active" />
                  </div>
                  <div style={{ fontSize:20, fontWeight:700, color:th.text }}>$15<span style={{ fontSize:11, fontWeight:400, color:th.textMuted }}>/user/mo</span></div>
                  <div style={{ fontSize:10, color:th.textSec, marginTop:4, marginBottom:12 }}>3 active users · $45/mo total</div>
                  <MockBtn label="Change plan" type="primary" small />
                </div>
                <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Usage</div>
                  {[
                    { label:"Members", used:"8", total:"25", pct:32, color:T.brand },
                    { label:"Storage", used:"600MB", total:"1GB", pct:60, color:T.cyan },
                    { label:"AI Tokens", used:"4.2K", total:"10K", pct:42, color:T.amber },
                  ].map(u => (
                    <div key={u.label} style={{ marginBottom:6 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:th.textSec, marginBottom:2 }}>
                        <span>{u.label}</span><span>{u.used} / {u.total}</span>
                      </div>
                      <div style={{ height:6, borderRadius:3, background:th.muted, overflow:"hidden" }}>
                        <div style={{ width:`${u.pct}%`, height:"100%", borderRadius:3, background:u.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16, marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ fontSize:18 }}>💳</div>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:th.text }}>Payment Method</div>
                      <div style={{ fontSize:10, color:th.textMuted, marginTop:2 }}>Visa ending in 4242 · Expires 12/27</div>
                    </div>
                  </div>
                  <div style={{ fontSize:10, color:T.brand, cursor:"default", textDecoration:"underline" }}>Update</div>
                </div>
              </div>
              <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:10 }}>Recent Invoices</div>
              {!mob && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", gap:8, padding:"8px 12px", fontSize:9, fontWeight:600, color:th.textMuted, borderBottom:`1px solid ${th.border}`, marginBottom:2 }}>
                  <span>Date</span><span>Invoice #</span><span>Amount</span><span>Status</span><span />
                </div>
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                {invoices.map((inv,i) => (
                  <div key={i} style={{ display:"grid", gridTemplateColumns:mob?"1fr":"1fr 1fr 1fr 1fr 1fr", gap:8, padding:mob?"8px 0":"8px 12px", background:i%2===0?th.muted:"transparent", borderRadius:T.rsm, alignItems:"center", cursor:"default" }}>
                    <span style={{ fontSize:10, color:th.text }}>{inv.date}</span>
                    <span style={{ fontSize:9, color:th.textMuted }}>{inv.num}</span>
                    <span style={{ fontSize:10, color:th.text }}>{inv.amount}</span>
                    <div><MockBadge type="neutral" label={inv.status} style={{ background:inv.color+"20", color:inv.color }} /></div>
                    <div style={{ fontSize:10, color:T.brand, cursor:"default", textDecoration:"underline" }}>{mob?"PDF":"Download PDF"}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:12, textAlign:"center" }}>
                <span style={{ fontSize:11, color:T.brand, cursor:"default", textDecoration:"underline" }}>Download all invoices →</span>
              </div>
            </div>
          </ZiniPixelsShell>
        </WF>
      );
    },
  };

  return screens[screen] ? screens[screen]() : <div style={{ padding:40, color:th.text }}>Unknown screen: {screen}</div>;
}

// ─── MAIN EXPORT ────────────────────────────────────────────────────────────────
export default function ZiniPixelsUXWizard() {
  return (
    <InspectProvider>
      <ZiniPixelsWizardContent />
    </InspectProvider>
  );
}

function ZiniPixelsWizardContent() {
  const [dark, setDark] = useState(false);
  const [viewport, setViewport] = useState("desktop");
  const [slim, setSlim] = useState(false);
  const [section, setSection] = useState("flows");
  const [role, setRole] = useState("editor");
  const [listView, setListView] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const flowRef = useRef(null);
  const { mode: inspectMode, setMode } = useInspector();

  // Sync inspector mode with panel visibility
  const inspectorOpen = inspectMode !== "off";

  const th = useTheme(dark);
  const mob = viewport === "mobile";

  const sections = WIZARD_TABS;

  const currentFlowData = FLOWS[currentFlow];
  const currentStepData = currentFlowData?.steps[currentStep];
  const flowContext = currentFlowData && currentStepData ? {
    flowLabel: currentFlowData.label,
    stepLabel: currentStepData.label,
    stepNote: currentStepData.note,
    shell: currentStepData.shell,
  } : null;

  const showFlowSidebar = sidebarOpen && (section === "flows" || section === "ia");

  const flowSidebar = (
    <div style={{ display:"flex", flexDirection:"column", gap:2, fontSize:10 }}>
      {FLOWS.map((f, fi) => (
        <div key={f.id} role="button" tabIndex={0}
          onClick={() => { setCurrentFlow(fi); setCurrentStep(0); }}
          style={{ padding:"6px 10px", borderRadius:4, cursor:"pointer", background:fi===currentFlow?"rgba(94,76,226,0.12)":"transparent", color:fi===currentFlow?T.brand:th.textSec }}
        >
          <div style={{ fontWeight:fi===currentFlow?500:400, marginBottom:2 }}>{f.label}</div>
          <div style={{ fontSize:8, color:th.textMuted }}>{f.who} · {f.steps.length} steps</div>
        </div>
      ))}
    </div>
  );

  const inspectorPanel = inspectorOpen ? (
    <InspectorPanel dark={dark} embedded flowContext={flowContext} onClose={() => setMode("off")} />
  ) : null;

  return (
      <WizardChrome
        dark={dark}
        setDark={setDark}
        title="ZiniPixels"
        sections={sections}
        section={section}
        onSectionChange={setSection}
        viewport={viewport}
        onViewportChange={setViewport}
        sidebarOpen={sidebarOpen}
        onSidebarToggle={() => setSidebarOpen(v => !v)}
        showSidebarToggle={section === "flows" || section === "ia"}
        flowSidebar={showFlowSidebar ? flowSidebar : null}
        inspectorOpen={inspectorOpen}
        inspectorPanel={inspectorPanel}
      >
        {/* Step-to-screen mapping for flow wireframes */}
        {section === "flows" && (() => {
          const stepToScreen = {
            "step-sign-in":"dashboard-empty",
            "step-empty-dashboard":"dashboard-empty",
            "step-create-first-project":"create-project-modal",
            "step-project-created":"dashboard-populated",
            "step-existing-dashboard":"dashboard-populated",
            "step-create-modal":"create-project-modal",
            "step-project-added":"dashboard-populated",
            "step-click-project":"dashboard-populated",
            "step-project-view":"project-view-populated",
            "step-open-menu":"dashboard-populated",
            "step-rename-project":"rename-project-modal",
            "step-archive-project":"archive-project-modal",
            "step-delete-project":"delete-project-modal",
            "step-viewer-dashboard":"viewer-dashboard",
            "step-viewer-project-entry":"viewer-project-view",
            "step-canvas-open":"project-view-populated",
            "step-canvas-empty":"canvas-empty",
            "step-canvas-draw-shape":"canvas-shapes",
            "step-canvas-add-text":"canvas-shapes",
            "step-canvas-layers":"canvas-layers",
            "step-canvas-export":"canvas-export",
            "step-collab-open":"project-view-populated",
            "step-collab-cursors":"collab-cursors",
            "step-collab-conflict":"collab-cursors",
            "step-comment-create":"comment-create",
            "step-comment-thread":"comment-thread",
            "step-comment-review":"comment-review-mode",
            "step-sharing-open":"sharing-modal",
            "step-sharing-invite":"sharing-modal",
            "step-sharing-members":"members-list",
            "step-ai-open":"ai-panel",
            "step-ai-generate":"ai-generate",
            "step-ai-suggest":"ai-suggestions",
            "step-ds-open":"ds-panel",
            "step-ds-apply-token":"ds-tokens",
            "step-ds-component":"ds-components",
            "step-settings-open":"settings-general",
            "step-settings-general":"settings-general",
            "step-settings-billing":"settings-billing",
            "step-onboarding-start":"onboarding-welcome",
            "step-onboarding-plan":"onboarding-plan",
            "step-onboarding-payment":"onboarding-payment",
            "step-onboarding-complete":"onboarding-welcome",
          };
          const flow = FLOWS[currentFlow];
          if (!flow) return null;
          const currentScreenId = stepToScreen[flow.steps[currentStep]?.id] || "dashboard-empty";
          return (
            <div style={{ padding:16, background:th.bg, height:"100%", overflow:"auto" }}>
              <div style={{ maxWidth: mob ? "100%" : 800, margin:"0 auto" }}>
                {/* Flow title */}
                <div style={{ fontSize:14, fontWeight:600, color:th.text, marginBottom:8 }}>{flow.label}</div>
                {/* Step pills */}
                <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:16, background:th.surface, borderRadius:T.rmd, padding:12, border:`1px solid ${th.border}` }}>
                  {flow.steps.map((s, si) => (
                    <div key={s.id}
                      onClick={() => { setCurrentStep(si); }}
                      style={{ padding:"3px 8px", borderRadius:10, fontSize:9, cursor:"default", background:si===currentStep?T.brand:"transparent", color:si===currentStep?"#fff":th.textSec, border:`1px solid ${si===currentStep?T.brand:th.border}` }}
                    >{si+1}. {s.label}</div>
                  ))}
                </div>
                {/* Wireframe for current step — render directly, ViewportFrame not needed here (WF handles its own chrome) */}
                <ScreenWF dark={dark} viewport={viewport} screen={currentScreenId} slim={slim} role={role} />
                {/* Step note + controls */}
                <div style={{ marginTop:12, padding:"10px 12px", background:th.muted, borderRadius:T.rsm, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ fontSize:10, color:th.textSec, lineHeight:1.5, flex:1 }}>{flow.steps[currentStep].note}</div>
                  <div style={{ display:"flex", gap:4, marginLeft:12, alignItems:"center" }}>
                    <span style={{ fontSize:9, color:th.textMuted }}>Role:</span>
                    {["editor","viewer"].map(r => (
                      <span key={r} onClick={()=>setRole(r)} style={{ padding:"2px 5px", borderRadius:3, fontSize:9, cursor:"default", background:role===r?T.brandSoft:"transparent", color:role===r?T.brand:th.textMuted }}>{r}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* IA tab */}
        {section === "ia" && (
          <div style={{ padding:16, background:th.bg, height:"100%", overflow:"auto" }}>
            <div style={{ marginBottom:12 }}>
              <div style={{ display:"flex", gap:4, background:"rgba(128,128,128,0.08)", borderRadius:6, padding:2, width:"fit-content" }}>
                <div onClick={()=>setListView(false)} style={{ padding:"4px 10px", borderRadius:4, fontSize:11, cursor:"default", background:!listView?"rgba(128,128,128,0.12)":"transparent", color:"rgba(128,128,128,0.7)" }}>List</div>
                <div onClick={()=>setListView(true)} style={{ padding:"4px 10px", borderRadius:4, fontSize:11, cursor:"default", background:listView?"rgba(128,128,128,0.12)":"transparent", color:"rgba(128,128,128,0.7)" }}>Diagram</div>
              </div>
            </div>
            {listView ? (
              <div>
                {IA_GROUPS.map(g => (
                  <div key={g.id} style={{ marginBottom:20 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:8 }}>{g.icon} {g.label}</div>
                    <div style={{ fontSize:10, color:th.textMuted, marginBottom:6 }}>{g.note}</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                      {g.screens.map(s => (
                        <div key={s.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", background:th.surface, borderRadius:T.rsm, border:`1px solid ${th.border}`, fontSize:11, color:th.text }}>
                          <MockBadge type="neutral" label={s.tag} />
                          {s.label}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display:"flex", gap:24 }}>
                {IA_GROUPS.map(g => (
                  <div key={g.id} style={{ flex:1, minWidth:220 }}>
                    <Flowchart mode="board" dark={dark} groups={[{ id:g.id, label:g.label, icon:g.icon, items:g.screens.map(s => ({ id:s.id||s.label, label:s.label })) }]} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Routes tab */}
        {section === "routes" && (
          <div style={{ padding:16, background:th.bg, height:"100%", overflow:"auto" }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>📍 Routes</div>
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {ROUTES.map(r => (
                <div key={r.path} style={{ display:"grid", gridTemplateColumns:"180px 140px 60px 70px 1fr", gap:8, padding:"8px 12px", background:th.surface, borderRadius:T.rsm, border:`1px solid ${th.border}`, fontSize:10, color:th.text, alignItems:"center" }}>
                  <code style={{ color:T.brand }}>{r.path}</code>
                  <span>{r.comp}</span>
                  <MockBadge type="neutral" label={r.auth} />
                  <MockBadge type="neutral" label={r.shell} />
                  <span style={{ color:th.textSec }}>{r.note}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Folders tab */}
        {section === "folders" && (
          <div style={{ padding:16, background:th.bg, height:"100%", overflow:"auto" }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>📂 Folder structure</div>
            <div style={{ fontFamily:"monospace", fontSize:11, color:th.text, lineHeight:2 }}>
              <div>zinipixels/</div>
              <div style={{ paddingLeft:20 }}>├── apps/web/</div>
              <div style={{ paddingLeft:40 }}>├── app/page.tsx (dashboard)</div>
              <div style={{ paddingLeft:40 }}>├── app/project/[id]/page.tsx (project view)</div>
              <div style={{ paddingLeft:40 }}>├── app/api/ (serverless API routes)</div>
              <div style={{ paddingLeft:20 }}>├── ws-server/ (y-websocket)</div>
              <div style={{ paddingLeft:20 }}>├── drizzle/ (schema + migrations)</div>
              <div style={{ paddingLeft:20 }}>└── packages/shared/ (types)</div>
            </div>
          </div>
        )}

        {/* Shells tab */}
        {section === "shells" && (
          <div style={{ padding:16, background:th.bg, height:"100%", overflow:"auto", display:"flex", gap:16 }}>
            {SHELLS.map(s => (
              <div key={s.id} style={{ flex:1, maxWidth:400, background:th.surface, borderRadius:T.rmd, border:`1px solid ${th.border}`, padding:16 }}>
                <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:8 }}>{s.label}</div>
                <div style={{ fontSize:10, color:th.textMuted, marginBottom:8 }}>For: {s.who}</div>
                <div style={{ fontSize:10, color:th.textSec, lineHeight:1.8 }}>
                  <strong>Topbar:</strong> {s.topbar}<br />
                  <strong>Content:</strong> {s.content}<br />
                  <strong>Note:</strong> {s.note}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tech Stack tab */}
        {section === "tech-stack" && (
          <div style={{ padding:16, background:th.bg, height:"100%", overflow:"auto" }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>⚙️ Tech Stack</div>
            <div style={{ display:"grid", gridTemplateColumns:"150px 1fr", gap:"6px 16px", fontSize:10, color:th.text }}>
              {TECH_STACK.map(([key, val]) => (
                <>
                  <div style={{ fontWeight:500, color:th.textSec, textAlign:"right", padding:"4px 0" }}>{key}</div>
                  <div style={{ color:th.text, padding:"4px 0" }}>{val}</div>
                </>
              ))}
            </div>
          </div>
        )}

        {/* Configs tab */}
        {section === "configs" && (
          <div style={{ padding:16, background:th.bg, height:"100%", overflow:"auto" }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>⚙️ Configs</div>
            {CONFIGS.map(c => (
              <div key={c.name} style={{ display:"flex", gap:12, padding:"8px 12px", background:th.surface, borderRadius:T.rsm, border:`1px solid ${th.border}`, fontSize:10, color:th.text, marginBottom:4, alignItems:"center" }}>
                <div style={{ fontWeight:600, minWidth:140 }}>{c.label}</div>
                <div>Projects: {c.projects}</div>
                <div>Files/project: {c.filesPerProject}</div>
                <div>Members: {c.members}</div>
                <div>Storage: {c.storage}</div>
                <div>AI: {c.aiTokens}/mo</div>
              </div>
            ))}
          </div>
        )}
      </WizardChrome>
  );
}
